// export default CheckoutPage;

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaShoppingCart,
  FaTruck,
  FaCreditCard,
  FaClipboardCheck,
  FaCheck,
  FaTimes,
  FaCopy,
} from "react-icons/fa";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  addDoc,
  setDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";

import { useMyContext } from "../Context/MyContext";
import ShippingAddress from "./ShippingAddress";
import { toast } from "react-toastify";
import { createVirtualAccount } from "../api/vas";

const stepsFull = [
  { key: "cart", label: "Cart", icon: <FaShoppingCart /> },
  { key: "shipping", label: "Shipping", icon: <FaTruck /> },
  { key: "review", label: "Review", icon: <FaClipboardCheck /> },
  { key: "payment", label: "Payment", icon: <FaCreditCard /> },
];
const stepsPaymentOnly = [
  { key: "payment", label: "Payment", icon: <FaCreditCard /> },
];

// ✅ VAT SETTINGS (0.4% capped at ₦300)
const VAT_RATE = 0.004;
const VAT_CAP = 300;

// ✅ VA Expiry Window (how long customer has to transfer before expiry)
const VA_EXPIRY_MINUTES = 60; // 60 minutes = 1 hour

// ✅ TEST SWITCH: set true to request STATIC VA (no singleDepositLimit / no expireAt)
// Set back to false for normal DYNAMIC per-order/shipment virtual accounts.
const USE_STATIC_VA_FOR_TEST = true;

// ✅ CHAREX FIRESTORE ROOT
// All reads and writes from this component now stay under:
// charex/main/users/{uid}/...
const CHAREX_COLLECTION = "charex";
const CHAREX_ROOT_DOCUMENT = "main";

const charexUserRef = (uid) =>
  doc(db, CHAREX_COLLECTION, CHAREX_ROOT_DOCUMENT, "users", uid);

const charexUserOrdersCollection = (uid) =>
  collection(
    db,
    CHAREX_COLLECTION,
    CHAREX_ROOT_DOCUMENT,
    "users",
    uid,
    "orders",
  );

const charexUserOrderRef = (uid, orderId) =>
  doc(
    db,
    CHAREX_COLLECTION,
    CHAREX_ROOT_DOCUMENT,
    "users",
    uid,
    "orders",
    orderId,
  );

const charexUserNotesCollection = (uid) =>
  collection(
    db,
    CHAREX_COLLECTION,
    CHAREX_ROOT_DOCUMENT,
    "users",
    uid,
    "notes",
  );

const getCharexOrderPath = (uid, orderId) =>
  `${CHAREX_COLLECTION}/${CHAREX_ROOT_DOCUMENT}/users/${uid}/orders/${orderId}`;

// ✅ local persistence key (per user)
const getPendingCheckoutCacheKey = (uid) => `charex_pending_checkout_va_${uid}`;

// ✅ local persistence key for last payment result (success/failure)
// This lets the customer close the site, make a transfer, reload later,
// and still see whether the payment succeeded or failed.
const getCheckoutPaymentResultCacheKey = (uid) =>
  `charex_checkout_payment_result_${uid}`;

// ✅ Helper: generate random 11-digit BVN (first digit 1–9)
const generateRandomBvn = () => {
  let bvn = "";
  for (let i = 0; i < 11; i++) {
    const digit =
      i === 0
        ? Math.floor(Math.random() * 9) + 1
        : Math.floor(Math.random() * 10);
    bvn += String(digit);
  }
  return bvn;
};

// ✅ Helper: make sure a shippingAddress has a valid BVN (behind the scenes)
const ensureBvnForUser = async (userId, shippingAddress = {}) => {
  const current = String(shippingAddress?.bvn || "").trim();
  if (/^\d{11}$/.test(current)) return shippingAddress;

  const newBvn = generateRandomBvn();
  const updatedAddress = { ...(shippingAddress || {}), bvn: newBvn };

  try {
    await setDoc(
      charexUserRef(userId),
      { shippingAddress: updatedAddress },
      { merge: true },
    );
  } catch (e) {
    console.warn(
      "ensureBvnForUser: could not save BVN to Firestore:",
      e?.message || e,
    );
  }

  return updatedAddress;
};

// ✅ Helper: format expireAt like "yyyy-MM-dd'T'HH:mm:ss.SSS+0100"
const formatExpireAtWithOffset = (dateObj) => {
  const pad2 = (n) => String(n).padStart(2, "0");
  const pad3 = (n) => String(n).padStart(3, "0");

  const d = dateObj;
  const yyyy = d.getFullYear();
  const MM = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const HH = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  const SSS = pad3(d.getMilliseconds());

  const tzOffsetMin = -d.getTimezoneOffset(); // minutes east of UTC
  const sign = tzOffsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(tzOffsetMin);
  const tzh = pad2(Math.floor(abs / 60));
  const tzm = pad2(abs % 60);

  return `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}.${SSS}${sign}${tzh}${tzm}`;
};

// ✅ Helper: parse expireAt that may look like "2026-02-20T10:30:00.000+0100"
const parseExpireAtToDate = (expireAtStr) => {
  const s = String(expireAtStr || "").trim();
  if (!s) return null;

  // Convert +0100 to +01:00 to be more widely parseable
  const fixed = s.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const d = new Date(fixed);
  if (!isNaN(d.getTime())) return d;

  // Fallback: try raw
  const d2 = new Date(s);
  if (!isNaN(d2.getTime())) return d2;

  return null;
};

// ✅ Helper: compute remaining time until expiry
const getExpiryRemaining = (expireAtStr) => {
  const d = parseExpireAtToDate(expireAtStr);
  if (!d)
    return { ok: false, msLeft: 0, minutesLeft: 0, hoursLeft: 0, label: "" };

  const msLeft = d.getTime() - Date.now();
  const safeMs = Math.max(0, msLeft);

  const minutesLeft = Math.ceil(safeMs / 60000);
  const hoursLeft = Math.floor(minutesLeft / 60);
  const minsRemainder = minutesLeft % 60;

  let label = "";
  if (minutesLeft <= 0) label = "Expired";
  else if (hoursLeft <= 0)
    label = `${minutesLeft} minute${minutesLeft === 1 ? "" : "s"} left`;
  else
    label = `${hoursLeft} hour${hoursLeft === 1 ? "" : "s"} ${
      minsRemainder ? `${minsRemainder} min` : ""
    } left`.trim();

  return { ok: true, msLeft: safeMs, minutesLeft, hoursLeft, label };
};

/**
 * ✅ VAT calc (0.4% capped at ₦300)
 * - VAT is calculated strictly from "what is to be paid" (the payable base amount),
 *   then ADDED to that payable base to get the final amount.
 */
const calcVat = (amountToBePaidBeforeVat) => {
  const base = Number(amountToBePaidBeforeVat || 0);
  if (!Number.isFinite(base) || base <= 0) return 0;
  const raw = base * VAT_RATE;
  const capped = Math.min(raw, VAT_CAP);
  return Math.round(capped);
};

// ✅ Helper: yyyy-MM-dd for reconcile endpoint
const formatYyyyMmDd = (d) => {
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// ✅ Helper: Prefer new fullAddress but keep old format as fallback
const getReadableShippingAddress = (shippingAddress = {}) => {
  const full = String(shippingAddress?.fullAddress || "").trim();
  if (full) return full;

  const fallback = [
    shippingAddress?.address,
    shippingAddress?.city,
    shippingAddress?.state,
    shippingAddress?.country,
    shippingAddress?.zip,
  ]
    .filter((x) => String(x || "").trim())
    .join(", ")
    .trim();

  return fallback;
};

const formatNaira = (val) => {
  const n = Number(val || 0);
  if (!Number.isFinite(n)) return "₦0";
  return `₦${n.toLocaleString()}`;
};

// ✅ Friendly error mapping for quota / VAS failures
const getFriendlyVasErrorMessage = (err) => {
  const msg = String(err?.message || err || "");
  const lower = msg.toLowerCase();

  // Firestore / GCP quota exhausted
  if (
    lower.includes("resource_exhausted") ||
    lower.includes("quota exceeded") ||
    err?.code === 8
  ) {
    return "Payment service is temporarily busy (quota reached). Please try again in a few minutes.";
  }

  // generic
  return "Failed to start payment. Please try again.";
};

// ✅ Remove values Firestore cannot store while preserving Firestore sentinels.
// Some products/addresses contain optional undefined fields, and that can make
// only particular orders fail even though other orders work normally.
const sanitizeForFirestore = (value) => {
  if (
    value === undefined ||
    typeof value === "function" ||
    typeof value === "symbol"
  ) {
    return undefined;
  }

  if (value === null) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeForFirestore(entry))
      .filter((entry) => entry !== undefined);
  }

  if (typeof value === "object") {
    const prototype = Object.getPrototypeOf(value);

    // Keep Firestore FieldValue/Timestamp objects and other class instances intact.
    if (prototype !== Object.prototype && prototype !== null) return value;

    return Object.entries(value).reduce((clean, [key, entry]) => {
      const sanitized = sanitizeForFirestore(entry);
      if (sanitized !== undefined) clean[key] = sanitized;
      return clean;
    }, {});
  }

  return value;
};

// ✅ Never place large temporary/base64 image bytes inside an order document.
// Product URLs are retained; browser blob URLs and data URLs are excluded.
const getSafeOrderImage = (imageValue) => {
  const image = String(imageValue || "").trim();
  if (!image) return "";

  const lower = image.toLowerCase();
  if (lower.startsWith("data:") || lower.startsWith("blob:")) return "";
  if (image.length > 5000) return "";

  return image;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [paymentReference, setPaymentReference] = useState(null);

  const {
    currentUser,
    cart,
    shipmentFees,
    clearShipmentFees,
    setShipmentFeeStatusPayment,

    // ✅ OPTIONAL cart clear helpers (depends on how your context is built)
    clearCart,
    clearCartItems,
    setCart,
    dispatch,
  } = useMyContext();

  // ✅ robust cart clear (works with many context implementations)
  const safeClearCart = () => {
    try {
      if (typeof clearCart === "function") return clearCart();
      if (typeof clearCartItems === "function") return clearCartItems();
      if (typeof setCart === "function") return setCart([]);
      if (typeof dispatch === "function") {
        dispatch({ type: "CLEAR_CART" });
        dispatch({ type: "cart/clear" });
        dispatch({ type: "EMPTY_CART" });
        return;
      }

      try {
        localStorage.removeItem("cart");
        localStorage.removeItem("CART");
      } catch {}
    } catch (e) {
      console.warn("safeClearCart failed:", e?.message || e);
    }
  };

  // ============================
  // ✅ Detect payment mode (KEEP THIS STYLE)
  // ============================
  const modeFromQuery = String(searchParams.get("mode") || "").toLowerCase();
  const modeFromState = String(location?.state?.mode || "").toLowerCase();

  const isShipmentMode =
    modeFromQuery === "shipment" || modeFromState === "shipment";
  const isBalanceMode =
    modeFromQuery === "balance" || modeFromState === "balance";

  // Payment-only UI for shipment + balance
  const isPaymentOnlyMode = isShipmentMode || isBalanceMode;

  // Steps
  const steps = isPaymentOnlyMode ? stepsPaymentOnly : stepsFull;

  // ============================
  // ✅ IDs + amounts from route
  // ============================
  const shipmentOrderId = String(
    location?.state?.orderId || searchParams.get("orderId") || "",
  ).trim();
  const shipmentOrderPath = String(location?.state?.orderPath || "").trim(); // optional

  const shipmentAmountFromState = Number(
    location?.state?.amount ??
      location?.state?.total ??
      location?.state?.totalAmount,
  );
  const shipmentAmountFromQuery = Number(searchParams.get("amount") || 0);
  const shipmentAmountHint =
    Number.isFinite(shipmentAmountFromState) && shipmentAmountFromState > 0
      ? shipmentAmountFromState
      : Number.isFinite(shipmentAmountFromQuery) && shipmentAmountFromQuery > 0
        ? shipmentAmountFromQuery
        : 0;

  // ✅ Balance mode uses same query keys
  const balanceOrderId = String(
    location?.state?.orderId || searchParams.get("orderId") || "",
  ).trim();

  const balanceAmountFromState = Number(
    location?.state?.amount ??
      location?.state?.total ??
      location?.state?.totalAmount,
  );
  const balanceAmountFromQuery = Number(searchParams.get("amount") || 0);
  const balanceAmountHint =
    Number.isFinite(balanceAmountFromState) && balanceAmountFromState > 0
      ? balanceAmountFromState
      : Number.isFinite(balanceAmountFromQuery) && balanceAmountFromQuery > 0
        ? balanceAmountFromQuery
        : 0;

  // ---------------- UI states ----------------
  const [currentStep, setCurrentStep] = useState(
    isPaymentOnlyMode ? "payment" : "shipping",
  );
  const [shippingAddress, setShippingAddress] = useState(null);

  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // ✅ permanent note lock
  const [noteLocked, setNoteLocked] = useState(false);

  // ✅ VAS states
  const [vaLoading, setVaLoading] = useState(false);
  const [virtualAccount, setVirtualAccount] = useState(null);
  const [showVAModal, setShowVAModal] = useState(false);

  // ✅ track last pending order id
  const [pendingOrderId, setPendingOrderId] = useState(null);

  // store expireAt for UI
  const [vaExpireAt, setVaExpireAt] = useState(null);

  // ✅ remaining time label for UI
  const [vaRemainingLabel, setVaRemainingLabel] = useState("");

  // ✅ reconcile loading
  const [reconLoading, setReconLoading] = useState(false);

  // ✅ load docs
  const [shipmentOrderDoc, setShipmentOrderDoc] = useState(null);
  const [shipmentAmount, setShipmentAmount] = useState(shipmentAmountHint);

  const [balanceOrderDoc, setBalanceOrderDoc] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState(balanceAmountHint);

  // ✅ Thank you modal
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [thankYouData, setThankYouData] = useState({
    amount: 0,
    mode: "order", // "order" | "shipment" | "balance"
    orderId: "",
  });

  // ✅ Failure modal for persisted/reloaded payment failure or expired session
  const [showPaymentFailureModal, setShowPaymentFailureModal] = useState(false);
  const [paymentFailureData, setPaymentFailureData] = useState({
    amount: 0,
    mode: "order", // "order" | "shipment" | "balance"
    orderId: "",
    message: "Payment was not confirmed.",
  });

  // ✅ guards so init effects don't fight each other
  const [restoringPendingVA, setRestoringPendingVA] = useState(true);
  const [pendingRestored, setPendingRestored] = useState(false);

  // ✅ HARD guard against double-click / multiple inflight requests
  const payNowInFlightRef = useRef(false);

  // ✅ AUTO PAYMENT POLLING GUARDS
  // Poll every 10 seconds. After 2 minutes, ask customer if they are still making payment.
  // If they continue, poll for another 2 minutes. If no inflow after that, clear and go home.
  const autoPaymentPollTimerRef = useRef(null);
  const autoPaymentPollStartRef = useRef(0);
  const autoPaymentPollRoundRef = useRef(1);
  const autoPaymentPollStoppedRef = useRef(false);
  const autoPaymentPollInFlightRef = useRef(false);
  const autoPaymentPollPromptOpenRef = useRef(false);

  // -------------------- VARIANTS HELPERS --------------------
  const normalizeVariants = (item) => {
    if (!item) return null;

    const cleanObj = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
      const out = {};
      Object.entries(obj).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const s = String(v).trim();
        if (!s) return;
        out[String(k)] = s;
      });
      return Object.keys(out).length ? out : null;
    };

    const fromVariantObject = cleanObj(item.variant);
    if (fromVariantObject) return fromVariantObject;

    const fromVariantsObject = cleanObj(item.variants);
    if (fromVariantsObject) return fromVariantsObject;

    const fromSelectedOptions = cleanObj(item.selectedOptions);
    if (fromSelectedOptions) return fromSelectedOptions;

    const fromOptions = cleanObj(item.options);
    if (fromOptions) return fromOptions;

    const fromAttributes = cleanObj(item.attributes);
    if (fromAttributes) return fromAttributes;

    if (typeof item.variantName === "string" && item.variantName.trim()) {
      return { name: item.variantName.trim() };
    }

    if (typeof item.variant === "string" && item.variant.trim()) {
      return { name: item.variant.trim() };
    }

    if (Array.isArray(item.variants) && item.variants.length) {
      const txt = item.variants
        .map((x) => (typeof x === "string" ? x.trim() : ""))
        .filter(Boolean)
        .join(", ");
      if (txt) return { name: txt };
    }

    return null;
  };

  const formatVariantsText = (variantsObj) => {
    if (!variantsObj || typeof variantsObj !== "object") return "";
    const entries = Object.entries(variantsObj)
      .filter(
        ([_, v]) => v !== undefined && v !== null && String(v).trim() !== "",
      )
      .map(([k, v]) => `${k}: ${v}`);
    return entries.length ? entries.join(", ") : "";
  };

  // ✅ Compare a saved pending order with the current cart before reusing it.
  // This prevents a payment retry from silently creating a second order.
  const getCartSignature = (items = []) =>
    JSON.stringify(
      (Array.isArray(items) ? items : []).map((item) => {
        const variantsObj = normalizeVariants(item);
        return {
          productId: String(item?.productId || item?.id || "").trim(),
          price: Number(item?.price || 0),
          quantity: Math.max(1, Number(item?.quantity) || 1),
          variantText:
            String(item?.variantText || "").trim() ||
            formatVariantsText(variantsObj),
        };
      }),
    );

  // ✅ Identify which order we should lock notes against
  const orderIdForNotes = useMemo(() => {
    if (isShipmentMode) return shipmentOrderId || "";
    if (isBalanceMode) return balanceOrderId || "";
    return pendingOrderId || "";
  }, [
    isShipmentMode,
    shipmentOrderId,
    isBalanceMode,
    balanceOrderId,
    pendingOrderId,
  ]);

  // ✅ helper: persist pending checkout state locally
  const persistPendingCheckoutCache = (payload) => {
    try {
      const uid = currentUser?.uid;
      if (!uid) return;
      const key = getPendingCheckoutCacheKey(uid);

      if (!payload) {
        localStorage.removeItem(key);
        return;
      }

      localStorage.setItem(
        key,
        JSON.stringify({
          ...payload,
          savedAt: Date.now(),
        }),
      );
    } catch (e) {
      console.warn("persistPendingCheckoutCache failed:", e?.message || e);
    }
  };

  // ✅ helper: persist final payment result so user sees it after reload
  const persistPaymentResultCache = (payload) => {
    try {
      const uid = currentUser?.uid;
      if (!uid) return;
      const key = getCheckoutPaymentResultCacheKey(uid);

      if (!payload) {
        localStorage.removeItem(key);
        return;
      }

      localStorage.setItem(
        key,
        JSON.stringify({
          ...payload,
          savedAt: Date.now(),
        }),
      );
    } catch (e) {
      console.warn("persistPaymentResultCache failed:", e?.message || e);
    }
  };

  const getPaymentModeForCurrentPage = () =>
    isBalanceMode ? "balance" : isShipmentMode ? "shipment" : "order";

  const getCurrentOrderIdForPayment = () =>
    isBalanceMode
      ? balanceOrderId
      : isShipmentMode
        ? shipmentOrderId
        : pendingOrderId;

  // ✅ helper: clear pending markers after confirm/cancel (NORMAL checkout resume)
  const clearPendingPaymentPersistence = async (opts = {}) => {
    const { orderId = "", keepOrderDoc = false } = opts || {};
    const uid = currentUser?.uid;

    try {
      if (!uid) return;

      persistPendingCheckoutCache(null);

      // Clear user-level pointer
      await setDoc(
        charexUserRef(uid),
        { pendingOrderId: null, updatedAt: serverTimestamp() },
        { merge: true },
      );

      // Optional marker on order doc (does not delete the order)
      if (!keepOrderDoc && orderId) {
        await setDoc(
          charexUserOrderRef(uid, orderId),
          {
            updatedAt: serverTimestamp(),
            checkoutState: { pendingVA: false, closedAt: serverTimestamp() },
          },
          { merge: true },
        );
      }
    } catch (e) {
      console.warn("clearPendingPaymentPersistence failed:", e?.message || e);
    }
  };

  // ✅ helper: extract VA from order doc (supports multiple shapes)
  const getVaFromOrderDoc = (order, which = "order") => {
    if (!order) return null;

    if (which === "shipment") {
      const shipPay = order?.shipmentPayment?.payment || {};
      const vNUBAN = String(
        shipPay?.vaNuban ||
          shipPay?.vaNUBAN ||
          shipPay?.vNUBAN ||
          shipPay?.vNuban ||
          "",
      )
        .replace(/\D/g, "")
        .trim();
      if (!vNUBAN) return null;

      return {
        vNUBAN,
        accountName:
          shipPay?.vaAccountName || shipPay?.accountName || "Customer",
        type: shipPay?.vaType || shipPay?.type || "DYNAMIC",
        createdAt: shipPay?.vaCreatedAt || null,
        expireAt: shipPay?.vaExpireAt || null,
      };
    }

    if (which === "balance") {
      const balPay = order?.balancePayment?.payment || {};
      const vNUBAN = String(
        balPay?.vaNuban ||
          balPay?.vaNUBAN ||
          balPay?.vNUBAN ||
          balPay?.vNuban ||
          "",
      )
        .replace(/\D/g, "")
        .trim();
      if (!vNUBAN) return null;

      return {
        vNUBAN,
        accountName: balPay?.vaAccountName || balPay?.accountName || "Customer",
        type: balPay?.vaType || balPay?.type || "DYNAMIC",
        createdAt: balPay?.vaCreatedAt || null,
        expireAt: balPay?.vaExpireAt || null,
      };
    }

    // default "order"
    const pay = order?.payment || {};
    const raw = pay?.rawPayload || {};

    const vNUBAN = String(
      pay?.vaNuban ||
        pay?.vaNUBAN ||
        pay?.vNUBAN ||
        pay?.vNuban ||
        raw?.vaNuban ||
        raw?.vaNUBAN ||
        raw?.vNUBAN ||
        raw?.vNuban ||
        "",
    )
      .replace(/\D/g, "")
      .trim();

    if (!vNUBAN) return null;

    return {
      vNUBAN,
      accountName: pay?.vaAccountName || raw?.vaAccountName || "Customer",
      type: pay?.vaType || raw?.vaType || raw?.vaRequestedType || "DYNAMIC",
      createdAt: pay?.vaCreatedAt || raw?.vaCreatedAt || null,
      expireAt: pay?.vaExpireAt || raw?.vaExpireAt || null,
    };
  };

  // -------------------- Load shipping info --------------------
  useEffect(() => {
    if (!currentUser) return;

    (async () => {
      try {
        const snap = await getDoc(charexUserRef(currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          if (data.shippingAddress) {
            const ensuredAddress = await ensureBvnForUser(
              currentUser.uid,
              data.shippingAddress,
            );
            setShippingAddress(ensuredAddress);

            // ✅ Only auto-jump to review if we are not restoring a pending VA session
            if (
              !isPaymentOnlyMode &&
              !pendingRestored &&
              !pendingOrderId &&
              !virtualAccount?.vNUBAN
            ) {
              setCurrentStep("review");
            }
          }
        }
      } catch {
        // silent
      }
    })();
  }, [
    currentUser,
    isPaymentOnlyMode,
    pendingRestored,
    pendingOrderId,
    virtualAccount?.vNUBAN,
  ]);

  // ✅ RESTORE pending VA / pending order for NORMAL checkout users
  useEffect(() => {
    if (!currentUser || isPaymentOnlyMode) {
      setRestoringPendingVA(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const uid = currentUser.uid;
        let orderIdToRestore = "";

        // 1) Try user doc pointer first
        const userSnap = await getDoc(charexUserRef(uid));
        const userData = userSnap.exists() ? userSnap.data() || {} : {};
        orderIdToRestore = String(userData?.pendingOrderId || "").trim();

        // 2) Fallback to localStorage cache
        if (!orderIdToRestore) {
          try {
            const raw = localStorage.getItem(getPendingCheckoutCacheKey(uid));
            if (raw) {
              const cached = JSON.parse(raw);
              orderIdToRestore = String(cached?.orderId || "").trim();
            }
          } catch {}
        }

        if (!orderIdToRestore) {
          if (!cancelled) {
            setRestoringPendingVA(false);
            setPendingRestored(false);
          }
          return;
        }

        const orderRef = charexUserOrderRef(uid, orderIdToRestore);
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          try {
            await setDoc(
              charexUserRef(uid),
              { pendingOrderId: null },
              { merge: true },
            );
          } catch {}
          persistPendingCheckoutCache(null);

          if (!cancelled) {
            setRestoringPendingVA(false);
            setPendingRestored(false);
          }
          return;
        }

        const order = orderSnap.data() || {};
        const paid =
          String(order?.orderStatus || "").toLowerCase() === "paid" ||
          String(order?.payment?.status || "").toLowerCase() === "paid" ||
          !!order?.payment?.paidAt;

        const restoredVa = getVaFromOrderDoc(order, "order");

        if (paid) {
          try {
            await setDoc(
              charexUserRef(uid),
              { pendingOrderId: null },
              { merge: true },
            );
          } catch {}
          persistPendingCheckoutCache(null);

          if (!cancelled) {
            openThankYouModal({
              amount: Number(
                order?.payment?.amount || order?.payableTotal || 0,
              ),
              mode: "order",
              orderId: orderIdToRestore,
            });
            setRestoringPendingVA(false);
            setPendingRestored(false);
          }
          return;
        }

        if (restoredVa?.vNUBAN) {
          if (cancelled) return;

          setPendingOrderId(orderIdToRestore);
          setVirtualAccount(restoredVa);
          setVaExpireAt(restoredVa?.expireAt || null);
          setCurrentStep("payment");
          setShowVAModal(true);
          setPendingRestored(true);

          persistPendingCheckoutCache({
            orderId: orderIdToRestore,
            orderPath:
              order?.orderPath || getCharexOrderPath(uid, orderIdToRestore),
            va: restoredVa,
            mode: "order",
          });
        } else {
          // The order was successfully created but VA generation may have
          // failed. Keep its ID available so Pay Now retries this same order.
          if (!cancelled) {
            setPendingOrderId(orderIdToRestore);
            setPendingRestored(false);
          }

          persistPendingCheckoutCache({
            orderId: orderIdToRestore,
            orderPath:
              order?.orderPath || getCharexOrderPath(uid, orderIdToRestore),
            mode: "order",
          });
        }
      } catch (e) {
        console.warn("Restore pending VA failed:", e?.message || e);
      } finally {
        if (!cancelled) setRestoringPendingVA(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, isPaymentOnlyMode]);

  // ✅ Load shipment order doc (shipment mode)
  useEffect(() => {
    if (!isShipmentMode || !currentUser || !shipmentOrderId) return;

    (async () => {
      try {
        const orderRef = charexUserOrderRef(currentUser.uid, shipmentOrderId);
        const snap = await getDoc(orderRef);
        if (!snap.exists()) {
          toast.error("Shipment order not found");
          return;
        }

        const data = snap.data() || {};
        const composed = { id: shipmentOrderId, ...data };
        setShipmentOrderDoc(composed);

        const feeFromOrder = Number(
          data?.shipmentPayment?.amount ??
            data?.shipment?.fee ??
            data?.shipmentFee ??
            0,
        );

        if (Number.isFinite(feeFromOrder) && feeFromOrder > 0)
          setShipmentAmount(feeFromOrder);
        else if (shipmentAmountHint > 0) setShipmentAmount(shipmentAmountHint);

        setPendingOrderId(shipmentOrderId);

        const shipState = String(
          data?.shipmentPayment?.state ||
            data?.shipmentPayment?.payment?.state ||
            "",
        ).toLowerCase();
        const shipPaid = shipState === "paid";
        const restoredShipVa = getVaFromOrderDoc(composed, "shipment");

        if (shipPaid) {
          openThankYouModal({
            amount: Number(
              data?.shipmentPayment?.payment?.amount ||
                data?.shipmentPayment?.payableTotal ||
                data?.shipmentPayment?.amount ||
                shipmentAmountHint ||
                0,
            ),
            mode: "shipment",
            orderId: shipmentOrderId,
          });
        } else if (restoredShipVa?.vNUBAN) {
          setVirtualAccount(restoredShipVa);
          setVaExpireAt(restoredShipVa?.expireAt || null);
          setShowVAModal(true);
        }

        setCurrentStep("payment");
      } catch (e) {
        console.error("Load shipment order failed:", e);
        toast.error("Unable to load shipment order");
      }
    })();
  }, [isShipmentMode, currentUser, shipmentOrderId, shipmentAmountHint]);

  // ✅ Load balance order doc (balance mode)
  useEffect(() => {
    if (!isBalanceMode || !currentUser || !balanceOrderId) return;

    (async () => {
      try {
        const orderRef = charexUserOrderRef(currentUser.uid, balanceOrderId);
        const snap = await getDoc(orderRef);
        if (!snap.exists()) {
          toast.error("Balance order not found");
          return;
        }

        const data = snap.data() || {};
        const composed = { id: balanceOrderId, ...data };
        setBalanceOrderDoc(composed);

        const fromDoc = Number(data?.balancePayment?.amount ?? 0);
        const resolved =
          Number.isFinite(balanceAmountHint) && balanceAmountHint > 0
            ? balanceAmountHint
            : Number.isFinite(fromDoc) && fromDoc > 0
              ? fromDoc
              : 0;

        setBalanceAmount(resolved);

        const balState = String(
          data?.balancePayment?.state ||
            data?.balancePayment?.payment?.state ||
            "",
        ).toLowerCase();
        const balPaid = balState === "paid";
        const restoredBalVa = getVaFromOrderDoc(composed, "balance");

        if (balPaid) {
          openThankYouModal({
            amount: Number(
              data?.balancePayment?.payment?.amount ||
                data?.balancePayment?.payableTotal ||
                data?.balancePayment?.amount ||
                balanceAmountHint ||
                0,
            ),
            mode: "balance",
            orderId: balanceOrderId,
          });
        } else if (restoredBalVa?.vNUBAN) {
          setVirtualAccount(restoredBalVa);
          setVaExpireAt(restoredBalVa?.expireAt || null);
          setShowVAModal(true);
        }

        setCurrentStep("payment");
      } catch (e) {
        console.error("Load balance order failed:", e);
        toast.error("Unable to load balance order");
      }
    })();
  }, [isBalanceMode, currentUser, balanceOrderId, balanceAmountHint]);

  // ✅ Load "noteLocked" from the order doc so it stays disabled permanently
  useEffect(() => {
    if (!currentUser) return;
    if (!orderIdForNotes) {
      setNoteLocked(false);
      return;
    }

    (async () => {
      try {
        const ref = charexUserOrderRef(currentUser.uid, orderIdForNotes);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setNoteLocked(false);
          return;
        }

        const data = snap.data() || {};
        const locked =
          !!data?.note?.locked || !!data?.noteLocked || !!data?.noteSent;
        setNoteLocked(locked);
      } catch (e) {
        console.warn("Load note lock failed:", e?.message || e);
        setNoteLocked(false);
      }
    })();
  }, [currentUser, orderIdForNotes]);

  // ✅ Update remaining expiry label live
  useEffect(() => {
    if (!vaExpireAt) {
      setVaRemainingLabel("");
      return;
    }

    const tick = () => {
      const r = getExpiryRemaining(vaExpireAt);
      setVaRemainingLabel(r?.ok ? r.label : "");
    };

    tick();
    const t = setInterval(tick, 15 * 1000);
    return () => clearInterval(t);
  }, [vaExpireAt]);

  const handleAddressSaved = async () => {
    if (!currentUser) return;

    const snap = await getDoc(charexUserRef(currentUser.uid));
    if (snap.exists()) {
      const data = snap.data();
      if (data.shippingAddress) {
        const ensuredAddress = await ensureBvnForUser(
          currentUser.uid,
          data.shippingAddress,
        );
        setShippingAddress(ensuredAddress);
      }
    }

    setCurrentStep("review");
  };

  const stepIndex = steps.findIndex((s) => s.key === currentStep);
  const progressWidth = `${((stepIndex + 1) / steps.length) * 100}%`;

  // -------------------- Totals --------------------
  const cartTotalAmount =
    cart?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;

  const cartShipmentFee =
    shipmentFees?.reduce((sum, item) => {
      const quantity = item.quantity || 1;
      return sum + (item.shipmentFee || 0) * quantity;
    }, 0) || 0;

  const cartGrandTotal = cartTotalAmount + cartShipmentFee;

  const payableBaseAmount = useMemo(() => {
    if (isBalanceMode) return Number(balanceAmount || 0);
    if (isShipmentMode) return Number(shipmentAmount || 0);
    return Number(cartGrandTotal || 0);
  }, [
    isBalanceMode,
    balanceAmount,
    isShipmentMode,
    shipmentAmount,
    cartGrandTotal,
  ]);

  const payableVatAmount = useMemo(
    () => calcVat(payableBaseAmount),
    [payableBaseAmount],
  );

  const payableAmount = useMemo(() => {
    const base = Number(payableBaseAmount || 0);
    const vat = Number(payableVatAmount || 0);
    return (
      (Number.isFinite(base) ? base : 0) + (Number.isFinite(vat) ? vat : 0)
    );
  }, [payableBaseAmount, payableVatAmount]);

  const payableLabel = isBalanceMode
    ? "Balance"
    : isShipmentMode
      ? "Shipment Fee"
      : "Grand Total";

  // -------------------- Save note (PERMANENT LOCK per ORDER) --------------------
  const handleSaveNote = async (order = null) => {
    if (!note.trim()) {
      toast.info("Note is optional. Type something to send.");
      return;
    }

    if (!currentUser) {
      toast.error("Please sign in");
      return;
    }

    const orderId = String(order?.id || orderIdForNotes || "").trim();
    if (!orderId) {
      toast.info(
        "Please click Pay Now first to create the order, then you can add an optional note.",
      );
      return;
    }

    if (noteLocked) {
      toast.info("Note already sent for this order.");
      return;
    }

    setSavingNote(true);

    try {
      const uid = currentUser.uid;

      await addDoc(charexUserNotesCollection(uid), {
        note: note.trim(),
        userId: uid,
        userName:
          currentUser?.displayName ||
          currentUser?.name ||
          currentUser?.email ||
          "Guest",
        userEmail: currentUser?.email || "not-provided",
        userRole: currentUser?.role || "user",

        orderId,
        customerName:
          order?.customer?.name ||
          order?.userName ||
          currentUser?.name ||
          "Guest",
        customerPhone: shippingAddress?.phone || null,

        timestamp: serverTimestamp(),
      });

      await setDoc(
        charexUserOrderRef(uid, orderId),
        {
          updatedAt: serverTimestamp(),
          note: { locked: true, text: note.trim(), sentAt: serverTimestamp() },
          noteLocked: true,
          noteSent: true,
        },
        { merge: true },
      );

      toast.success("Note sent ✅");
      setNote("");
      setNoteLocked(true);
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error(error?.message || "Error sending note. Please try again.");
    } finally {
      setSavingNote(false);
    }
  };

  // ================================
  // CREATE PENDING ORDER (NORMAL CHECKOUT ONLY)
  // ================================
  const createPendingOrder = async ({ paymentRef, meta = {} }) => {
    const uid = String(currentUser?.uid || "").trim();
    if (!uid) throw new Error("User not authenticated");

    // Do not clear the previous pending pointer before the replacement order is
    // safely committed. If this write fails, the previous order remains resumable.
    const normalizedCart = (cart || []).map((item, index) => {
      const price = Number(item?.price);
      const quantity = Math.max(1, Number(item?.quantity) || 1);

      if (!Number.isFinite(price) || price < 0) {
        throw new Error(
          `Invalid price for ${item?.name || item?.title || `cart item ${index + 1}`}`,
        );
      }

      const variantsObj = normalizeVariants(item);
      const variantText = variantsObj ? formatVariantsText(variantsObj) : "";

      return sanitizeForFirestore({
        productId: String(item?.id || item?.productId || "").trim(),
        name: item?.name || item?.title || "Item",
        price,
        quantity,
        image: getSafeOrderImage(item?.image),
        category: item?.category || "",
        status: item?.status || "Available",
        ...(variantsObj ? { variants: variantsObj } : {}),
        ...(variantText ? { variantText } : {}),
        ...(item?.sku ? { sku: item.sku } : {}),
      });
    });

    if (!normalizedCart.length) {
      throw new Error("Your cart is empty. No order was created.");
    }

    const totalAmountCalc = normalizedCart.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.quantity),
      0,
    );

    const shipmentFeeCalc =
      shipmentFees?.reduce((sum, item) => {
        const quantity = item.quantity || 1;
        return sum + (item.shipmentFee || 0) * quantity;
      }, 0) || 0;

    const payableBaseCalc = totalAmountCalc + shipmentFeeCalc;
    const vatAmountCalc = calcVat(payableBaseCalc);
    const payableTotalCalc = payableBaseCalc + vatAmountCalc;

    const readableAddress = getReadableShippingAddress(shippingAddress || {});
    const customerAddress =
      readableAddress ||
      `${shippingAddress?.address || ""}, ${shippingAddress?.city || ""}, ${shippingAddress?.state || ""}, ${shippingAddress?.country || ""}`.trim();

    const orderData = {
      userId: uid,

      customer: {
        name:
          `${shippingAddress?.title || ""} ${shippingAddress?.firstName || ""} ${shippingAddress?.lastName || ""}`.trim() ||
          currentUser.displayName ||
          "Guest User",
        email: shippingAddress?.email || currentUser.email || "unknown",
        phone: shippingAddress?.phone || "N/A",
        address: customerAddress,
      },

      userEmail: currentUser.email,
      userName: currentUser.displayName || "Guest User",

      cart: normalizedCart,

      shippingAddress,

      totalAmount: totalAmountCalc,
      shipmentFee: shipmentFeeCalc,

      grandTotal: payableBaseCalc,

      vat: { rate: VAT_RATE, cap: VAT_CAP, amount: vatAmountCalc },

      payableTotal: payableTotalCalc,

      // ✅ UNIQUE ORDER (prevents any accidental merge/debug issues)
      orderFingerprint: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,

      payment: {
        provider: "UBA_VAS",
        reference: paymentRef,
        status: "Pending",
        amount: payableTotalCalc,
        currency: "NGN",
        initiatedAt: serverTimestamp(),
        rawPayload: {
          ...(meta || null),
          vatRate: VAT_RATE,
          vatCap: VAT_CAP,
          vatAmount: vatAmountCalc,
          payableBase: payableBaseCalc,
          payableTotal: payableTotalCalc,
          vaRequestedType: USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC",
        },
      },

      checkoutState: { pendingVA: true, createdAt: serverTimestamp() },

      orderStatus: "Pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Generate the ID first, then commit the complete order in one acknowledged
    // Firestore write. The UI never receives an order ID from this function
    // unless the order document itself has been accepted by Firestore.
    const orderRef = doc(charexUserOrdersCollection(uid));
    const orderPath = getCharexOrderPath(uid, orderRef.id);
    const completeOrderData = sanitizeForFirestore({
      ...orderData,
      orderPath,
    });

    try {
      await setDoc(orderRef, completeOrderData);
    } catch (error) {
      console.error("ORDER_DOCUMENT_WRITE_FAILED", {
        code: error?.code || "unknown",
        message: error?.message || String(error),
        uid,
        orderId: orderRef.id,
        orderPath,
        itemCount: normalizedCart.length,
      });
      throw error;
    }

    try {
      await setDoc(
        charexUserRef(uid),
        {
          pendingOrderId: orderRef.id,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (e) {
      console.warn("Could not persist user.pendingOrderId:", e?.message || e);
    }

    persistPendingCheckoutCache({
      orderId: orderRef.id,
      orderPath,
      mode: "order",
    });

    console.info("ORDER_DOCUMENT_SAVED", {
      orderId: orderRef.id,
      orderPath,
      itemCount: normalizedCart.length,
    });

    return orderRef.id;
  };

  // ================================
  // VAS - BUILD PAYLOAD
  // - STATIC: omit singleDepositLimit + expireAt
  // - DYNAMIC: include singleDepositLimit + expireAt
  // ================================
  const buildVasPayload = (
    addressOverride,
    customerReferenceOverride,
    opts = {},
  ) => {
    const addr = addressOverride || shippingAddress || {};
    const bvn = addr.bvn || "";

    const firstName =
      addr.firstName || currentUser?.displayName?.split(" ")?.[0] || "Customer";
    const lastName =
      addr.lastName ||
      currentUser?.displayName?.split(" ")?.slice(1).join(" ") ||
      "User";

    const resolvedAccountType = String(
      opts?.accountType || "DYNAMIC",
    ).toUpperCase();

    const payload = {
      bvn,
      firstName,
      lastName,
      middleName: addr.middleName || "",
      accountName: `${firstName} ${lastName}`.trim(),
      email: addr.email || currentUser?.email || "",
      phone: addr.phone || "",
      productType: "CHAREX",

      // ✅ CRITICAL: send this so backend won't default to DYNAMIC
      accountType: resolvedAccountType,

      customerReference: customerReferenceOverride || addr.phone,

      merchant: { code: process.env.MERCHANT_CODE || "FA9D4" },
    };

    // ✅ DYNAMIC account only when both fields are provided
    if (
      resolvedAccountType !== "STATIC" &&
      opts?.singleDepositLimit != null &&
      opts?.expireAt
    ) {
      payload.singleDepositLimit = String(opts.singleDepositLimit);
      payload.expireAt = String(opts.expireAt);
    }

    if (process.env.VAS_PRODUCT_CODE) {
      payload.meta = { product_code: process.env.VAS_PRODUCT_CODE };
    }

    return payload;
  };

  // ==========================================================
  // ✅ Save VA to ORDER for NORMAL payment
  // ==========================================================
  const saveVaToOrderOrThrow = async ({
    uid,
    orderId,
    payloadCustomerReference,
    paymentRef,
    amount,
    expireAt,
    va,
  }) => {
    const orderDocRef = charexUserOrderRef(uid, orderId);

    await setDoc(
      orderDocRef,
      {
        updatedAt: serverTimestamp(),
        orderStatus: "Pending",
        checkoutState: { pendingVA: true, updatedAt: serverTimestamp() },
        payment: {
          provider: "UBA_VAS",
          reference: payloadCustomerReference || orderId,
          paymentRef: paymentRef || null,
          status: "Pending",
          currency: "NGN",
          amount: Number(amount) || 0,

          vaNuban: String(va?.vNUBAN || ""),
          vaAccountName: va?.accountName || null,
          vaType: va?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC"),
          vaExpireAt: expireAt || null,
          vaCreatedAt: va?.createdAt || null,

          vaExpiryMinutes: expireAt ? VA_EXPIRY_MINUTES : null,
          vaRequestedType: USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC",
        },
      },
      { merge: true },
    );

    try {
      await setDoc(
        charexUserRef(uid),
        { pendingOrderId: orderId, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (e) {
      console.warn(
        "saveVaToOrderOrThrow user pendingOrderId failed:",
        e?.message || e,
      );
    }

    const check = await getDoc(orderDocRef);
    const saved = check.exists() ? check.data() : null;
    const savedVa = String(saved?.payment?.vaNuban || "").replace(/\D/g, "");

    if (!savedVa) {
      throw new Error(
        "VA was generated but could NOT be saved into the ORDER document. Check Firestore rules for charex/main/users/{uid}/orders/{orderId} update permission.",
      );
    }

    persistPendingCheckoutCache({
      orderId,
      orderPath: getCharexOrderPath(uid, orderId),
      mode: "order",
      va: {
        vNUBAN: String(va?.vNUBAN || ""),
        accountName: va?.accountName || null,
        type: va?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC"),
        createdAt: va?.createdAt || null,
        expireAt: expireAt || null,
      },
    });
  };

  // ==========================================================
  // ✅ Save VA to ORDER for SHIPMENT payment
  // ==========================================================
  const saveShipmentVaToOrderOrThrow = async ({
    uid,
    orderId,
    orderPathMaybe,
    payloadCustomerReference,
    paymentRef,
    amount,
    expireAt,
    va,
    vatAmount,
  }) => {
    const orderDocRef = charexUserOrderRef(uid, orderId);

    await setDoc(
      orderDocRef,
      {
        updatedAt: serverTimestamp(),
        orderPath: orderPathMaybe || getCharexOrderPath(uid, orderId),
        shipmentPayment: {
          enabled: true,
          amount: Number(amount) || 0,
          vat: { rate: VAT_RATE, cap: VAT_CAP, amount: Number(vatAmount) || 0 },
          payableTotal: Number(amount || 0) + Number(vatAmount || 0),
          state: "pending",
          payment: {
            provider: "UBA_VAS",
            reference: payloadCustomerReference || `${orderId}-SHIP`,
            paymentRef: paymentRef || null,
            state: "pending",
            currency: "NGN",
            amount: Number(amount || 0) + Number(vatAmount || 0),
            initiatedAt: serverTimestamp(),

            vaNuban: String(va?.vNUBAN || ""),
            vaAccountName: va?.accountName || null,
            vaType: va?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC"),
            vaExpireAt: expireAt || null,
            vaCreatedAt: va?.createdAt || null,

            vaExpiryMinutes: expireAt ? VA_EXPIRY_MINUTES : null,
            vaRequestedType: USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC",
          },
        },
      },
      { merge: true },
    );

    const check = await getDoc(orderDocRef);
    const saved = check.exists() ? check.data() : null;
    const savedVa = String(
      saved?.shipmentPayment?.payment?.vaNuban || "",
    ).replace(/\D/g, "");

    if (!savedVa) {
      throw new Error(
        "Shipment VA was generated but could NOT be saved into the ORDER document. Check Firestore rules for shipmentPayment updates.",
      );
    }
  };

  // ==========================================================
  // ✅ Save VA to ORDER for BALANCE payment
  // ==========================================================
  const saveBalanceVaToOrderOrThrow = async ({
    uid,
    orderId,
    orderPathMaybe,
    payloadCustomerReference,
    paymentRef,
    amount,
    expireAt,
    va,
    vatAmount,
  }) => {
    const orderDocRef = charexUserOrderRef(uid, orderId);

    await setDoc(
      orderDocRef,
      {
        updatedAt: serverTimestamp(),
        orderPath: orderPathMaybe || getCharexOrderPath(uid, orderId),
        balancePayment: {
          enabled: true,
          amount: Number(amount) || 0,
          vat: { rate: VAT_RATE, cap: VAT_CAP, amount: Number(vatAmount) || 0 },
          payableTotal: Number(amount || 0) + Number(vatAmount || 0),
          state: "pending",
          payment: {
            provider: "UBA_VAS",
            reference: payloadCustomerReference || `${orderId}-BAL`,
            paymentRef: paymentRef || null,
            state: "pending",
            currency: "NGN",
            amount: Number(amount || 0) + Number(vatAmount || 0),
            initiatedAt: serverTimestamp(),

            vaNuban: String(va?.vNUBAN || ""),
            vaAccountName: va?.accountName || null,
            vaType: va?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC"),
            vaExpireAt: expireAt || null,
            vaCreatedAt: va?.createdAt || null,

            vaExpiryMinutes: expireAt ? VA_EXPIRY_MINUTES : null,
            vaRequestedType: USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC",
          },
        },
      },
      { merge: true },
    );

    const check = await getDoc(orderDocRef);
    const saved = check.exists() ? check.data() : null;
    const savedVa = String(
      saved?.balancePayment?.payment?.vaNuban || "",
    ).replace(/\D/g, "");

    if (!savedVa) {
      throw new Error(
        "Balance VA was generated but could NOT be saved into the ORDER document. Check Firestore rules for balancePayment updates.",
      );
    }
  };

  // ================================
  // VAS - CREATE DYNAMIC VA PER ORDER
  // ================================
  const createDynamicVirtualAccountForOrder = async (
    addressOverride,
    orderId,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const amount = Number(amountToPay || 0);
    if (!Number.isFinite(amount) || amount <= 0)
      throw new Error("Invalid amount. Please try again.");

    const expireAt = formatExpireAtWithOffset(
      new Date(Date.now() + VA_EXPIRY_MINUTES * 60 * 1000),
    );

    const payload = buildVasPayload(addressOverride, orderId, {
      accountType: "DYNAMIC",
      singleDepositLimit: amount,
      expireAt,
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in VA payload:", payload.bvn);
      throw new Error("Unable to generate virtual account. Please try again.");
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get virtual account number. Please try again.",
      );

    await saveVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      payloadCustomerReference: payload?.customerReference || orderId,
      paymentRef,
      amount,
      expireAt,
      va,
    });

    return { ...va, expireAt };
  };

  const createShipmentVirtualAccountForExistingOrder = async (
    addressOverride,
    orderId,
    orderPathMaybe,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const payableBase = Number(amountToPay || 0);
    if (!Number.isFinite(payableBase) || payableBase <= 0)
      throw new Error("Invalid shipment amount. Please try again.");

    const vatAmount = calcVat(payableBase);
    const amountWithVat = payableBase + vatAmount;

    const expireAt = formatExpireAtWithOffset(
      new Date(Date.now() + VA_EXPIRY_MINUTES * 60 * 1000),
    );
    const shipmentRef = `${orderId}-SHIP`;

    const payload = buildVasPayload(addressOverride, shipmentRef, {
      accountType: "DYNAMIC",
      singleDepositLimit: amountWithVat,
      expireAt,
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in shipment VA payload:", payload.bvn);
      throw new Error(
        "Unable to generate shipment virtual account. Please try again.",
      );
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get shipment virtual account number. Please try again.",
      );

    await saveShipmentVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      orderPathMaybe,
      payloadCustomerReference: payload?.customerReference || shipmentRef,
      paymentRef,
      amount: payableBase,
      vatAmount,
      expireAt,
      va,
    });

    return { ...va, expireAt };
  };

  const createBalanceVirtualAccountForExistingOrder = async (
    addressOverride,
    orderId,
    orderPathMaybe,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const payableBase = Number(amountToPay || 0);
    if (!Number.isFinite(payableBase) || payableBase <= 0)
      throw new Error("Invalid balance amount. Please try again.");

    const vatAmount = calcVat(payableBase);
    const amountWithVat = payableBase + vatAmount;

    const expireAt = formatExpireAtWithOffset(
      new Date(Date.now() + VA_EXPIRY_MINUTES * 60 * 1000),
    );
    const balanceRef = `${orderId}-BAL`;

    const payload = buildVasPayload(addressOverride, balanceRef, {
      accountType: "DYNAMIC",
      singleDepositLimit: amountWithVat,
      expireAt,
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in balance VA payload:", payload.bvn);
      throw new Error(
        "Unable to generate balance virtual account. Please try again.",
      );
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get balance virtual account number. Please try again.",
      );

    await saveBalanceVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      orderPathMaybe,
      payloadCustomerReference: payload?.customerReference || balanceRef,
      paymentRef,
      amount: payableBase,
      vatAmount,
      expireAt,
      va,
    });

    return { ...va, expireAt };
  };

  // ================================
  // VAS - CREATE STATIC VAs
  // ================================
  const createStaticVirtualAccountForOrder = async (
    addressOverride,
    orderId,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const amount = Number(amountToPay || 0);
    if (!Number.isFinite(amount) || amount <= 0)
      throw new Error("Invalid amount. Please try again.");

    const payload = buildVasPayload(addressOverride, orderId, {
      accountType: "STATIC",
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in STATIC VA payload:", payload.bvn);
      throw new Error("Unable to generate virtual account. Please try again.");
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get static virtual account number. Please try again.",
      );

    await saveVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      payloadCustomerReference: payload?.customerReference || orderId,
      paymentRef,
      amount,
      expireAt: null,
      va,
    });

    return { ...va, expireAt: null };
  };

  const createStaticShipmentVirtualAccountForExistingOrder = async (
    addressOverride,
    orderId,
    orderPathMaybe,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const payableBase = Number(amountToPay || 0);
    if (!Number.isFinite(payableBase) || payableBase <= 0)
      throw new Error("Invalid shipment amount. Please try again.");

    const vatAmount = calcVat(payableBase);
    const shipmentRef = `${orderId}-SHIP`;

    const payload = buildVasPayload(addressOverride, shipmentRef, {
      accountType: "STATIC",
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in STATIC shipment VA payload:", payload.bvn);
      throw new Error(
        "Unable to generate shipment virtual account. Please try again.",
      );
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get shipment virtual account number. Please try again.",
      );

    await saveShipmentVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      orderPathMaybe,
      payloadCustomerReference: payload?.customerReference || shipmentRef,
      paymentRef,
      amount: payableBase,
      vatAmount,
      expireAt: null,
      va,
    });

    return { ...va, expireAt: null };
  };

  const createStaticBalanceVirtualAccountForExistingOrder = async (
    addressOverride,
    orderId,
    orderPathMaybe,
    paymentRef,
    amountToPay,
  ) => {
    if (!currentUser) throw new Error("Please sign in first.");

    const payableBase = Number(amountToPay || 0);
    if (!Number.isFinite(payableBase) || payableBase <= 0)
      throw new Error("Invalid balance amount. Please try again.");

    const vatAmount = calcVat(payableBase);
    const balanceRef = `${orderId}-BAL`;

    const payload = buildVasPayload(addressOverride, balanceRef, {
      accountType: "STATIC",
    });

    if (!payload.bvn || !/^\d{11}$/.test(String(payload.bvn))) {
      console.error("Invalid BVN in STATIC balance VA payload:", payload.bvn);
      throw new Error(
        "Unable to generate balance virtual account. Please try again.",
      );
    }

    const response = await createVirtualAccount(payload);
    const va = response?.data?.provider_response?.virtualAccount;

    if (!va?.vNUBAN)
      throw new Error(
        "Unable to get balance virtual account number. Please try again.",
      );

    await saveBalanceVaToOrderOrThrow({
      uid: currentUser.uid,
      orderId,
      orderPathMaybe,
      payloadCustomerReference: payload?.customerReference || balanceRef,
      paymentRef,
      amount: payableBase,
      vatAmount,
      expireAt: null,
      va,
    });

    return { ...va, expireAt: null };
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Copy failed", err);
      toast.error("Copy failed");
    }
  };

  const normalizePaymentMode = (mode) =>
    mode === "shipment" ? "shipment" : mode === "balance" ? "balance" : "order";

  const openThankYouModal = ({ amount, mode, orderId }) => {
    const normalizedMode = normalizePaymentMode(mode);
    const normalizedOrderId = String(orderId || "").trim();
    const normalizedAmount = Number(amount) || 0;

    setShowVAModal(false);
    setShowPaymentFailureModal(false);

    const payload = {
      status: "success",
      amount: normalizedAmount,
      mode: normalizedMode,
      orderId: normalizedOrderId,
    };

    setThankYouData(payload);
    persistPaymentResultCache(payload);
    setShowThankYouModal(true);
  };

  const openPaymentFailureModal = ({ amount, mode, orderId, message }) => {
    const normalizedMode = normalizePaymentMode(mode);
    const normalizedOrderId = String(orderId || "").trim();
    const normalizedAmount = Number(amount) || 0;

    setShowVAModal(false);
    setShowThankYouModal(false);

    const payload = {
      status: "failure",
      amount: normalizedAmount,
      mode: normalizedMode,
      orderId: normalizedOrderId,
      message: message || "Payment was not confirmed.",
    };

    setPaymentFailureData(payload);
    persistPaymentResultCache(payload);
    setShowPaymentFailureModal(true);
  };

  const handleThankYouOk = () => {
    setShowThankYouModal(false);
    persistPaymentResultCache(null);

    try {
      if (typeof clearShipmentFees === "function") clearShipmentFees();
    } catch {}

    navigate("/");
  };

  const handlePaymentFailureOk = () => {
    setShowPaymentFailureModal(false);
    persistPaymentResultCache(null);
    navigate("/");
  };

  // ✅ Optional cancel pending payment session (keeps order history, clears resume state)
  const handleCancelPendingPayment = async () => {
    try {
      const orderIdToCancel = isBalanceMode
        ? balanceOrderId
        : isShipmentMode
          ? shipmentOrderId
          : pendingOrderId;

      if (!isPaymentOnlyMode) {
        await clearPendingPaymentPersistence({
          orderId: orderIdToCancel,
          keepOrderDoc: false,
        });
      }

      setVirtualAccount(null);
      setVaExpireAt(null);
      setShowVAModal(false);

      if (!isPaymentOnlyMode) {
        setPendingOrderId(null);
        setPendingRestored(false);
        setCurrentStep(shippingAddress ? "review" : "shipping");
        openPaymentFailureModal({
          amount: payableAmount,
          mode: getPaymentModeForCurrentPage(),
          orderId: orderIdToCancel,
          message: "Payment session was cancelled before confirmation.",
        });
      } else {
        toast.info("Payment session closed.");
      }
    } catch (e) {
      console.error("Cancel pending payment failed:", e);
      toast.error("Unable to cancel pending payment session");
    }
  };

  // ================================
  // RECONCILE PAYMENT (supports shipment + balance)
  // ================================
  const reconcileOrderPayment = async () => {
    try {
      const uid = currentUser?.uid;

      const orderIdToCheck = isBalanceMode
        ? balanceOrderId
        : isShipmentMode
          ? shipmentOrderId
          : pendingOrderId;

      if (!uid || !orderIdToCheck) {
        toast.error("Missing order info");
        return;
      }

      setReconLoading(true);

      const orderRef = charexUserOrderRef(uid, orderIdToCheck);
      const snap = await getDoc(orderRef);

      if (!snap.exists()) {
        toast.error("Order not found");
        return;
      }

      const order = snap.data() || {};
      const orderPath =
        order?.orderPath || getCharexOrderPath(uid, orderIdToCheck);

      let vNUBAN = "";

      // 1) balance VA
      if (isBalanceMode) {
        vNUBAN = String(
          order?.balancePayment?.payment?.vaNuban ||
            order?.balancePayment?.payment?.vaNUBAN ||
            order?.balancePayment?.payment?.vNUBAN ||
            "",
        ).replace(/\D/g, "");
      }

      // 2) shipment VA
      if (!vNUBAN && isShipmentMode) {
        vNUBAN = String(
          order?.shipmentPayment?.payment?.vaNuban ||
            order?.shipmentPayment?.payment?.vaNUBAN ||
            order?.shipmentPayment?.payment?.vNUBAN ||
            "",
        ).replace(/\D/g, "");
      }

      // 3) normal payment VA
      if (!vNUBAN) {
        vNUBAN = String(
          order?.payment?.vaNuban ||
            order?.payment?.vaNUBAN ||
            order?.payment?.vNUBAN ||
            order?.payment?.vNuban ||
            order?.payment?.rawPayload?.vaNuban ||
            order?.payment?.rawPayload?.vaNUBAN ||
            order?.payment?.rawPayload?.vNUBAN ||
            "",
        ).replace(/\D/g, "");
      }

      // 4) fallback
      if (!vNUBAN) {
        vNUBAN = String(
          order?.vasVirtualAccount?.vNUBAN ||
            order?.vasVirtualAccount?.vaNuban ||
            virtualAccount?.vNUBAN ||
            virtualAccount?.vaNuban ||
            "",
        ).replace(/\D/g, "");
      }

      if (!vNUBAN) {
        toast.error("No virtual account number found on this order yet");
        return;
      }

      const today = new Date();
      const start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const startDate = formatYyyyMmDd(start);
      const endDate = formatYyyyMmDd(today);

      const mode = isBalanceMode
        ? "balance"
        : isShipmentMode
          ? "shipment"
          : "order";

      const res = await fetch("/.netlify/functions/query-inflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vNUBAN,
          orderPath,
          startDate,
          endDate,
          markPaid: true,
          userId: uid,
          orderId: orderIdToCheck,
          mode,
          paymentReference,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data?.ok && (data?.updated?.paid === true || data?.paid === true)) {
        if (!isPaymentOnlyMode) {
          await clearPendingPaymentPersistence({
            orderId: orderIdToCheck,
            keepOrderDoc: false,
          });
          setPendingOrderId(null);
          setVirtualAccount(null);
          setVaExpireAt(null);
          setPendingRestored(false);
          safeClearCart();
        }

        if (
          isShipmentMode &&
          typeof setShipmentFeeStatusPayment === "function"
        ) {
          try {
            setShipmentFeeStatusPayment(true);
          } catch {}
        }

        const amountFromRes =
          Number(
            data?.updated?.amount ??
              data?.amount ??
              data?.successful?.[0]?.amount,
          ) || null;

        openThankYouModal({
          amount: amountFromRes != null ? amountFromRes : payableAmount,
          mode,
          orderId: orderIdToCheck,
        });

        return;
      }

      if (
        data?.ok &&
        (data?.successfulCount === 0 || data?.updated?.paid === false)
      ) {
        toast.info("No successful inflow yet. Try again in 1–2 minutes.");
        return;
      }

      toast.error(data?.message || "Reconcile failed");
    } catch (e) {
      console.error("Reconcile error:", e);
      toast.error(e?.message || "Reconcile failed");
    } finally {
      setReconLoading(false);
    }
  };

  // ================================
  // AUTO PAYMENT POLLING - SILENT CHECK
  // ✅ Does not toast "No inflow yet"
  // ✅ Does not clear cart while still checking
  // ✅ Opens success modal once query-inflow confirms paid/partial inflow
  // ================================
  const silentAutoReconcilePayment = async () => {
    const uid = currentUser?.uid;

    const orderIdToCheck = isBalanceMode
      ? balanceOrderId
      : isShipmentMode
        ? shipmentOrderId
        : pendingOrderId;

    if (!uid || !orderIdToCheck) return false;

    try {
      const orderRef = charexUserOrderRef(uid, orderIdToCheck);
      const snap = await getDoc(orderRef);

      if (!snap.exists()) return false;

      const order = snap.data() || {};
      const orderPath =
        order?.orderPath || getCharexOrderPath(uid, orderIdToCheck);

      const mode = isBalanceMode
        ? "balance"
        : isShipmentMode
          ? "shipment"
          : "order";

      // ✅ If Firestore was already updated by webhook or another check, treat as success.
      const mainPaid =
        String(order?.orderStatus || "").toLowerCase() === "paid" ||
        String(order?.payment?.status || "").toLowerCase() === "paid" ||
        !!order?.payment?.paidAt;

      const shipmentPaid =
        String(order?.shipmentPayment?.state || "").toLowerCase() === "paid" ||
        String(order?.shipmentPayment?.payment?.state || "").toLowerCase() ===
          "paid" ||
        !!order?.shipmentPayment?.paidAt;

      const balancePaid =
        String(order?.balancePayment?.state || "").toLowerCase() === "paid" ||
        String(order?.balancePayment?.payment?.state || "").toLowerCase() ===
          "paid" ||
        !!order?.balancePayment?.paidAt;

      const alreadyPaidForThisMode = isBalanceMode
        ? balancePaid
        : isShipmentMode
          ? shipmentPaid
          : mainPaid;

      if (alreadyPaidForThisMode) {
        if (!isPaymentOnlyMode) {
          await clearPendingPaymentPersistence({
            orderId: orderIdToCheck,
            keepOrderDoc: false,
          });
          setPendingOrderId(null);
          setVirtualAccount(null);
          setVaExpireAt(null);
          setPendingRestored(false);
          safeClearCart();
        }

        if (
          isShipmentMode &&
          typeof setShipmentFeeStatusPayment === "function"
        ) {
          try {
            setShipmentFeeStatusPayment(true);
          } catch {}
        }

        openThankYouModal({
          amount: payableAmount,
          mode,
          orderId: orderIdToCheck,
        });

        return true;
      }

      let vNUBAN = "";
      let referenceToSend = "";

      if (isBalanceMode) {
        vNUBAN = String(
          order?.balancePayment?.payment?.vaNuban ||
            order?.balancePayment?.payment?.vaNUBAN ||
            order?.balancePayment?.payment?.vNUBAN ||
            "",
        ).replace(/\D/g, "");

        referenceToSend = String(
          order?.balancePayment?.payment?.reference ||
            order?.balancePayment?.payment?.paymentRef ||
            `${orderIdToCheck}-BAL`,
        ).trim();
      }

      if (!vNUBAN && isShipmentMode) {
        vNUBAN = String(
          order?.shipmentPayment?.payment?.vaNuban ||
            order?.shipmentPayment?.payment?.vaNUBAN ||
            order?.shipmentPayment?.payment?.vNUBAN ||
            "",
        ).replace(/\D/g, "");

        referenceToSend = String(
          order?.shipmentPayment?.payment?.reference ||
            order?.shipmentPayment?.payment?.paymentRef ||
            `${orderIdToCheck}-SHIP`,
        ).trim();
      }

      if (!vNUBAN) {
        vNUBAN = String(
          order?.payment?.vaNuban ||
            order?.payment?.vaNUBAN ||
            order?.payment?.vNUBAN ||
            order?.payment?.vNuban ||
            order?.payment?.rawPayload?.vaNuban ||
            order?.payment?.rawPayload?.vaNUBAN ||
            order?.payment?.rawPayload?.vNUBAN ||
            "",
        ).replace(/\D/g, "");

        referenceToSend = String(
          order?.payment?.reference ||
            order?.payment?.paymentRef ||
            paymentReference ||
            orderIdToCheck,
        ).trim();
      }

      if (!vNUBAN) {
        vNUBAN = String(
          order?.vasVirtualAccount?.vNUBAN ||
            order?.vasVirtualAccount?.vaNuban ||
            virtualAccount?.vNUBAN ||
            virtualAccount?.vaNuban ||
            "",
        ).replace(/\D/g, "");
      }

      if (!vNUBAN) return false;

      const today = new Date();
      const start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const res = await fetch("/.netlify/functions/query-inflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vNUBAN,
          vaNuban: vNUBAN,
          orderPath,
          startDate: formatYyyyMmDd(start),
          endDate: formatYyyyMmDd(today),
          markPaid: true,
          userId: uid,
          orderId: orderIdToCheck,
          mode,
          paymentReference: referenceToSend,
          reference: referenceToSend,
          customerReference: referenceToSend,
        }),
      });

      const data = await res.json().catch(() => ({}));
      const successfulArr = Array.isArray(data?.successful)
        ? data.successful
        : Array.isArray(data?.successfulInflows)
          ? data.successfulInflows
          : [];

      const successState = String(data?.updated?.state || "").toLowerCase();
      const hasConfirmedInflow =
        data?.ok &&
        (data?.updated?.paid === true ||
          data?.paid === true ||
          successState === "paid" ||
          successState === "partial" ||
          successfulArr.length > 0);

      if (!hasConfirmedInflow) return false;

      if (!isPaymentOnlyMode) {
        await clearPendingPaymentPersistence({
          orderId: orderIdToCheck,
          keepOrderDoc: false,
        });
        setPendingOrderId(null);
        setVirtualAccount(null);
        setVaExpireAt(null);
        setPendingRestored(false);
        safeClearCart();
      }

      if (isShipmentMode && typeof setShipmentFeeStatusPayment === "function") {
        try {
          setShipmentFeeStatusPayment(true);
        } catch {}
      }

      const amountFromRes =
        Number(
          data?.updated?.paidTotal ??
            data?.updated?.amount ??
            data?.amount ??
            successfulArr?.[0]?.amount,
        ) || null;

      openThankYouModal({
        amount: amountFromRes != null ? amountFromRes : payableAmount,
        mode,
        orderId: orderIdToCheck,
      });

      return true;
    } catch (e) {
      console.warn("Silent auto payment check failed:", e?.message || e);
      return false;
    }
  };

  // ================================
  // AUTO PAYMENT POLLING TIMER
  // ✅ Poll every 10 seconds while the payment modal is open.
  // ✅ After 2 minutes, ask if customer is still making payment.
  // ✅ IMPORTANT: do NOT clear the pending order just because the customer delayed.
  // Customer may close the browser, make transfer, then return later.
  // ================================
  useEffect(() => {
    const orderIdToWatch = isBalanceMode
      ? balanceOrderId
      : isShipmentMode
        ? shipmentOrderId
        : pendingOrderId;

    if (
      !showVAModal ||
      !virtualAccount?.vNUBAN ||
      !currentUser ||
      !orderIdToWatch
    ) {
      return undefined;
    }

    const stopAutoPolling = () => {
      autoPaymentPollStoppedRef.current = true;
      if (autoPaymentPollTimerRef.current) {
        clearInterval(autoPaymentPollTimerRef.current);
        autoPaymentPollTimerRef.current = null;
      }
    };

    const pauseAutoPollingWithoutClearing = () => {
      stopAutoPolling();
      toast.info(
        "Payment has not been confirmed yet. You can close this page and come back later; your payment session is saved.",
      );
    };

    autoPaymentPollStoppedRef.current = false;
    autoPaymentPollInFlightRef.current = false;
    autoPaymentPollPromptOpenRef.current = false;
    autoPaymentPollRoundRef.current = 1;
    autoPaymentPollStartRef.current = Date.now();

    const runAutoPoll = async () => {
      if (autoPaymentPollStoppedRef.current) return;
      if (autoPaymentPollInFlightRef.current) return;

      const elapsedMs = Date.now() - autoPaymentPollStartRef.current;

      if (elapsedMs >= 2 * 60 * 1000) {
        if (autoPaymentPollRoundRef.current === 1) {
          if (autoPaymentPollPromptOpenRef.current) return;
          autoPaymentPollPromptOpenRef.current = true;

          stopAutoPolling();

          const keepWaiting = window.confirm(
            "We have not confirmed your payment yet. Are you still making the payment? Click OK to keep checking for another 2 minutes, or Cancel to stop.",
          );

          autoPaymentPollPromptOpenRef.current = false;

          if (keepWaiting) {
            autoPaymentPollStoppedRef.current = false;
            autoPaymentPollRoundRef.current = 2;
            autoPaymentPollStartRef.current = Date.now();
            autoPaymentPollTimerRef.current = setInterval(
              runAutoPoll,
              10 * 1000,
            );
            return;
          }

          pauseAutoPollingWithoutClearing();
          return;
        }

        pauseAutoPollingWithoutClearing();
        return;
      }

      autoPaymentPollInFlightRef.current = true;
      const paid = await silentAutoReconcilePayment();
      autoPaymentPollInFlightRef.current = false;

      if (paid) {
        stopAutoPolling();
      }
    };

    autoPaymentPollTimerRef.current = setInterval(runAutoPoll, 10 * 1000);

    return () => {
      stopAutoPolling();
    };
  }, [
    showVAModal,
    virtualAccount?.vNUBAN,
    currentUser,
    pendingOrderId,
    shipmentOrderId,
    balanceOrderId,
    isShipmentMode,
    isBalanceMode,
    isPaymentOnlyMode,
    paymentReference,
    payableAmount,
    navigate,
  ]);

  // ================================
  // ✅ RELOAD RESUME CHECK
  // When customer closes the site to make a transfer and later reloads,
  // immediately check the saved pending order and show success/failure/pending.
  // ================================
  useEffect(() => {
    const orderIdToCheck = isBalanceMode
      ? balanceOrderId
      : isShipmentMode
        ? shipmentOrderId
        : pendingOrderId;

    if (!currentUser) return;

    // 1) Show last saved success/failure result after reload until customer closes it.
    try {
      const raw = localStorage.getItem(
        getCheckoutPaymentResultCacheKey(currentUser.uid),
      );
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.status === "success") {
          setThankYouData({
            amount: Number(cached?.amount || 0),
            mode: normalizePaymentMode(cached?.mode),
            orderId: String(cached?.orderId || ""),
          });
          setShowVAModal(false);
          setShowPaymentFailureModal(false);
          setShowThankYouModal(true);
          return;
        }
        if (cached?.status === "failure") {
          setPaymentFailureData({
            amount: Number(cached?.amount || 0),
            mode: normalizePaymentMode(cached?.mode),
            orderId: String(cached?.orderId || ""),
            message: cached?.message || "Payment was not confirmed.",
          });
          setShowVAModal(false);
          setShowThankYouModal(false);
          setShowPaymentFailureModal(true);
          return;
        }
      }
    } catch {}

    // 2) If a pending virtual account exists after reload, show it and immediately check inflow.
    if (!orderIdToCheck || !virtualAccount?.vNUBAN) return;

    let cancelled = false;

    (async () => {
      try {
        setShowVAModal(true);
        const paid = await silentAutoReconcilePayment();
        if (cancelled || paid) return;

        const expiry = getExpiryRemaining(vaExpireAt);
        if (vaExpireAt && expiry?.ok && expiry?.minutesLeft <= 0) {
          openPaymentFailureModal({
            amount: payableAmount,
            mode: getPaymentModeForCurrentPage(),
            orderId: orderIdToCheck,
            message:
              "This payment account has expired and payment was not confirmed.",
          });
        }
      } catch (e) {
        console.warn("Reload resume check failed:", e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser,
    pendingOrderId,
    shipmentOrderId,
    balanceOrderId,
    isShipmentMode,
    isBalanceMode,
    virtualAccount?.vNUBAN,
    vaExpireAt,
    payableAmount,
  ]);

  // ================================
  // PAY NOW FLOW
  // ✅ Upgraded:
  // - HARD guard against multiple clicks
  // - extra: re-check order doc before generating a new VA (reduces quota burn)
  // - friendly quota error messaging
  // ================================
  const handlePayNow = async () => {
    // hard lock
    if (payNowInFlightRef.current) return;

    // If VA creation fails after the order document was committed, retain and
    // report the exact saved order instead of making it look like it vanished.
    let createdOrderIdForAttempt = "";

    try {
      if (!currentUser) {
        toast.error("Please sign in to continue.");
        return;
      }

      if (!shippingAddress) {
        toast.error("Please fill your shipping address first.");
        if (!isPaymentOnlyMode) setCurrentStep("shipping");
        return;
      }

      // ✅ If already in loading state, bail
      if (vaLoading) return;

      // ✅ Lock immediately (prevents double click even before state updates)
      payNowInFlightRef.current = true;
      setVaLoading(true);

      // ✅ If a VA is already present in UI, reuse
      if (
        !isPaymentOnlyMode &&
        pendingOrderId &&
        virtualAccount?.vNUBAN &&
        false // disable reuse for fresh orders
      ) {
        setShowVAModal(true);
        setCurrentStep("payment");
        toast.info("Using your existing pending virtual account.");
        return;
      }

      // ✅ Ensure BVN on shipping profile
      let localAddress = await ensureBvnForUser(
        currentUser.uid,
        shippingAddress,
      );
      setShippingAddress(localAddress);

      // ✅ Generate payment reference and store in state
      const paymentRef =
        "VAS_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).slice(2, 10);
      setPaymentReference(paymentRef); // <-- STORE IT

      // ✅ BALANCE MODE (KEEP THIS STYLE)
      if (isBalanceMode) {
        if (!balanceOrderId) {
          toast.error("Missing balance order id");
          return;
        }

        // ✅ Re-check order doc to reuse VA (extra safety)
        try {
          const ref = charexUserOrderRef(currentUser.uid, balanceOrderId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const od = { id: balanceOrderId, ...(snap.data() || {}) };
            const existingBalanceVa = getVaFromOrderDoc(od, "balance");
            const balState = String(
              od?.balancePayment?.state ||
                od?.balancePayment?.payment?.state ||
                "",
            ).toLowerCase();
            if (existingBalanceVa?.vNUBAN && balState !== "paid") {
              setBalanceOrderDoc(od);
              setPendingOrderId(balanceOrderId);
              setVirtualAccount(existingBalanceVa);
              setVaExpireAt(existingBalanceVa?.expireAt || null);
              setShowVAModal(true);
              setCurrentStep("payment");
              toast.info("Using existing balance virtual account.");
              return;
            }
          }
        } catch {}

        const baseToPay = Number(balanceAmount || 0);
        if (!Number.isFinite(baseToPay) || baseToPay <= 0) {
          toast.error("Balance amount is invalid");
          return;
        }

        const va = USE_STATIC_VA_FOR_TEST
          ? await createStaticBalanceVirtualAccountForExistingOrder(
              localAddress,
              balanceOrderId,
              balanceOrderDoc?.orderPath,
              paymentRef,
              baseToPay,
            )
          : await createBalanceVirtualAccountForExistingOrder(
              localAddress,
              balanceOrderId,
              balanceOrderDoc?.orderPath,
              paymentRef,
              baseToPay,
            );

        setPendingOrderId(balanceOrderId);
        setVirtualAccount(va);
        setVaExpireAt(va?.expireAt || null);

        toast.success(
          USE_STATIC_VA_FOR_TEST
            ? "Static virtual account ready. Please transfer to the account shown."
            : "Balance virtual account ready. Please transfer to the account shown.",
        );

        setShowVAModal(true);
        setCurrentStep("payment");
        return;
      }

      // ✅ SHIPMENT MODE (KEEP THIS STYLE)
      if (isShipmentMode) {
        if (!shipmentOrderId) {
          toast.error("Missing shipment order id");
          return;
        }

        // ✅ Re-check order doc to reuse VA (extra safety)
        try {
          const ref = charexUserOrderRef(currentUser.uid, shipmentOrderId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const od = { id: shipmentOrderId, ...(snap.data() || {}) };
            const existingShipmentVa = getVaFromOrderDoc(od, "shipment");
            const shipState = String(
              od?.shipmentPayment?.state ||
                od?.shipmentPayment?.payment?.state ||
                "",
            ).toLowerCase();
            if (existingShipmentVa?.vNUBAN && shipState !== "paid") {
              setShipmentOrderDoc(od);
              setPendingOrderId(shipmentOrderId);
              setVirtualAccount(existingShipmentVa);
              setVaExpireAt(existingShipmentVa?.expireAt || null);
              setShowVAModal(true);
              setCurrentStep("payment");
              toast.info("Using existing shipment virtual account.");
              return;
            }
          }
        } catch {}

        const baseToPay = Number(shipmentAmount || 0);
        if (!Number.isFinite(baseToPay) || baseToPay <= 0) {
          toast.error("Shipment amount is invalid");
          return;
        }

        const va = USE_STATIC_VA_FOR_TEST
          ? await createStaticShipmentVirtualAccountForExistingOrder(
              localAddress,
              shipmentOrderId,
              shipmentOrderPath || shipmentOrderDoc?.orderPath,
              paymentRef,
              baseToPay,
            )
          : await createShipmentVirtualAccountForExistingOrder(
              localAddress,
              shipmentOrderId,
              shipmentOrderPath || shipmentOrderDoc?.orderPath,
              paymentRef,
              baseToPay,
            );

        setPendingOrderId(shipmentOrderId);
        setVirtualAccount(va);
        setVaExpireAt(va?.expireAt || null);

        toast.success(
          USE_STATIC_VA_FOR_TEST
            ? "Static virtual account ready. Please transfer to the account shown."
            : "Shipment virtual account ready. Please transfer to the account shown.",
        );

        setShowVAModal(true);
        setCurrentStep("payment");
        return;
      }

      // ✅ NORMAL MODE (KEEP THIS STYLE)
      const baseToPay = Number(cartGrandTotal || 0);
      if (!Number.isFinite(baseToPay) || baseToPay <= 0) {
        toast.error("Cart total is invalid.");
        return;
      }

      const vatAmount = calcVat(baseToPay);
      const paymentAmount = baseToPay + vatAmount;

      let orderId = "";

      // Reuse a previously committed unpaid order only when its cart and total
      // exactly match the current checkout. This covers VA/network retries
      // without creating duplicate orders or losing the earlier order pointer.
      if (pendingOrderId) {
        try {
          const existingRef = charexUserOrderRef(currentUser.uid, pendingOrderId);
          const existingSnap = await getDoc(existingRef);

          if (existingSnap.exists()) {
            const existingOrder = existingSnap.data() || {};
            const isPaid =
              String(existingOrder?.orderStatus || "").toLowerCase() ===
                "paid" ||
              String(existingOrder?.payment?.status || "").toLowerCase() ===
                "paid" ||
              !!existingOrder?.payment?.paidAt;
            const sameCart =
              getCartSignature(existingOrder?.cart || []) ===
              getCartSignature(cart || []);
            const sameAmount =
              Math.abs(
                Number(existingOrder?.payableTotal || 0) - paymentAmount,
              ) < 0.01;

            if (!isPaid && sameCart && sameAmount) {
              orderId = pendingOrderId;
              persistPendingCheckoutCache({
                orderId,
                orderPath:
                  existingOrder?.orderPath ||
                  getCharexOrderPath(currentUser.uid, orderId),
                mode: "order",
              });
              toast.info("Continuing your existing pending order.");
            }
          }
        } catch (error) {
          console.warn(
            "Could not inspect existing pending order; creating a new one:",
            error?.message || error,
          );
        }
      }

      if (!orderId) {
        orderId = await createPendingOrder({
          paymentRef,
          meta: {
            source: "Checkout Pay Now",
            baseToPay,
            vatAmount,
            amount: paymentAmount,
            vaRequestedType: USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC",
          },
        });
      }

      createdOrderIdForAttempt = orderId;

      setPendingOrderId(orderId);

      // ✅ EXTRA SAFETY: if something already wrote VA to this order, reuse it
      try {
        const ref = charexUserOrderRef(currentUser.uid, orderId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const od = { id: orderId, ...(snap.data() || {}) };
          const existingOrderVa = getVaFromOrderDoc(od, "order");
          const paid =
            String(od?.orderStatus || "").toLowerCase() === "paid" ||
            String(od?.payment?.status || "").toLowerCase() === "paid" ||
            !!od?.payment?.paidAt;

          if (!paid && existingOrderVa?.vNUBAN) {
            setVirtualAccount(existingOrderVa);
            setVaExpireAt(existingOrderVa?.expireAt || null);
            persistPendingCheckoutCache({
              orderId,
              orderPath: getCharexOrderPath(currentUser.uid, orderId),
              mode: "order",
              va: existingOrderVa,
            });
            toast.info("Using existing order virtual account.");
            setShowVAModal(true);
            setCurrentStep("payment");
            return;
          }
        }
      } catch {}

      const va = USE_STATIC_VA_FOR_TEST
        ? await createStaticVirtualAccountForOrder(
            localAddress,
            orderId,
            paymentRef,
            paymentAmount,
          )
        : await createDynamicVirtualAccountForOrder(
            localAddress,
            orderId,
            paymentRef,
            paymentAmount,
          );

      setVirtualAccount(va);
      setVaExpireAt(va?.expireAt || null);

      persistPendingCheckoutCache({
        orderId,
        orderPath: getCharexOrderPath(currentUser.uid, orderId),
        mode: "order",
        va: {
          vNUBAN: String(va?.vNUBAN || ""),
          accountName: va?.accountName || null,
          type: va?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC"),
          createdAt: va?.createdAt || null,
          expireAt: va?.expireAt || null,
        },
      });

      toast.success(
        USE_STATIC_VA_FOR_TEST
          ? "Static virtual account ready. Please transfer to the account shown."
          : "Virtual account ready. Please transfer to the account shown.",
      );

      setShowVAModal(true);
      setCurrentStep("payment");
      return;
    } catch (e) {
      console.error("PayNow flow error:", e);

      if (createdOrderIdForAttempt) {
        toast.error(
          `Order ${createdOrderIdForAttempt} was saved, but the payment account could not be prepared. Please retry payment for this same order; do not place another order.`,
        );
      } else {
        const errorCode = String(e?.code || "").toLowerCase();
        const errorMessage = String(e?.message || "").toLowerCase();

        if (
          errorCode.includes("permission-denied") ||
          errorMessage.includes("insufficient permissions")
        ) {
          toast.error(
            "The order was not saved because Firestore denied the write. Please contact the administrator.",
          );
        } else if (
          errorCode.includes("invalid-argument") ||
          errorMessage.includes("unsupported field value")
        ) {
          toast.error(
            "The order was not saved because one of the cart items contains invalid data. Please refresh the cart and try again.",
          );
        } else {
          toast.error(getFriendlyVasErrorMessage(e));
        }
      }
    } finally {
      setVaLoading(false);
      payNowInFlightRef.current = false;
    }
  };

  const goToNextStep = () => {
    if (isPaymentOnlyMode) return;
    const order = ["shipping", "review", "payment"];
    const currentIndex = order.indexOf(currentStep);
    if (currentIndex < order.length - 1)
      setCurrentStep(order[currentIndex + 1]);
  };

  const goToPreviousStep = () => {
    if (isPaymentOnlyMode) return;
    const order = ["shipping", "review", "payment"];
    const currentIndex = order.indexOf(currentStep);
    if (currentIndex > 0) setCurrentStep(order[currentIndex - 1]);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Tiny CSS for modal animations */}
      <style>{`
        @keyframes tyFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tyBackdrop { from { opacity: 0; backdrop-filter: blur(0px); } to { opacity: 1; backdrop-filter: blur(6px); } }
        @keyframes tyPop {
          0% { opacity: 0; transform: translateY(18px) scale(0.94); }
          60% { opacity: 1; transform: translateY(-2px) scale(1.015); }
          100% { opacity: 1; transform: translateY(0px) scale(1); }
        }
        @keyframes tyRing { 0% { transform: scale(0.75); opacity: 0.35; } 100% { transform: scale(1.55); opacity: 0; } }
        @keyframes tyCheck {
          0% { opacity: 0; transform: scale(0.55) rotate(-12deg); }
          65% { opacity: 1; transform: scale(1.12) rotate(3deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes tyFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes tyProgress { from { width: 0%; } to { width: 100%; } }
      `}</style>

      <div className="flex-1 overflow-y-auto pt-44 sm:pt-48 pb-20 px-4">
        {/* Progress bar */}
        <div className="max-w-4xl mx-auto mb-12 relative">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-300 -z-10" />
          <div
            className="absolute top-1/2 left-0 h-1 bg-purple-600 transition-all duration-500"
            style={{ width: progressWidth, transform: "translateY(-50%)" }}
          />
          <div className="flex justify-between items-center">
            {steps.map((step, i) => {
              const isCompleted = i < stepIndex;
              const isActive = step.key === currentStep;
              return (
                <div key={step.key} className="flex flex-col items-center z-10">
                  <div
                    className={`w-14 h-14 flex items-center justify-center rounded-full text-white text-xl shadow-lg ${
                      isCompleted
                        ? "bg-green-500"
                        : isActive
                          ? "bg-purple-600"
                          : "bg-gray-400"
                    } transition`}
                  >
                    {isCompleted ? <FaCheck /> : step.icon}
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-700">
                    {step.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ✅ Optional restore loading hint */}
        {!isPaymentOnlyMode && restoringPendingVA && (
          <div className="max-w-lg mx-auto mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Restoring your pending payment session...
          </div>
        )}

        {/* SHIPPING (disabled for shipment/balance mode) */}
        {!isPaymentOnlyMode && currentStep === "shipping" && (
          <div className="max-w-2xl mx-auto bg-white p-6 rounded-2xl shadow-md">
            <ShippingAddress
              user={currentUser}
              onSuccess={handleAddressSaved}
            />
          </div>
        )}

        {/* REVIEW (disabled for shipment/balance mode) */}
        {!isPaymentOnlyMode && currentStep === "review" && (
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-6 shadow-md text-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-center">
              Review & Confirm
            </h2>

            <div className="border-b pb-4 mb-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">
                Shipping Information
              </h3>
              <div className="text-gray-700 space-y-1">
                <p>
                  <strong>Name:</strong> {shippingAddress?.title}{" "}
                  {shippingAddress?.firstName} {shippingAddress?.lastName}
                </p>
                <p>
                  <strong>Phone:</strong> {shippingAddress?.phone}
                </p>
                <p>
                  <strong>Email:</strong> {shippingAddress?.email}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {getReadableShippingAddress(shippingAddress || {}) ||
                    `${shippingAddress?.address || ""}, ${shippingAddress?.city || ""}, ${
                      shippingAddress?.state || ""
                    }, ${shippingAddress?.country || ""}`.trim()}
                </p>
              </div>
            </div>

            <div className="border-b pb-4 mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">
                Your Cart
              </h3>
              {cart && cart.length > 0 ? (
                <div className="divide-y">
                  {cart.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between py-2 text-sm sm:text-base"
                    >
                      <div className="pr-3">
                        <div>
                          {item.name} × {item.quantity}
                        </div>
                      </div>
                      <span>
                        ₦{(item.price * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  ))}

                  <div className="flex justify-between pt-3 text-sm text-gray-700">
                    <span>Shipment Fee:</span>
                    <span>₦{cartShipmentFee?.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between pt-2 text-sm text-gray-700">
                    <span>VAT (0.4% capped ₦300):</span>
                    <span>₦{calcVat(cartGrandTotal).toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between font-semibold text-gray-800 pt-3 text-lg">
                    <span>Grand Total (Incl. VAT):</span>
                    <span>
                      ₦
                      {(
                        cartGrandTotal + calcVat(cartGrandTotal)
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Your cart is empty.</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
              <button
                onClick={goToPreviousStep}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold w-full sm:w-auto transition"
              >
                Back
              </button>
              <button
                onClick={goToNextStep}
                className="bg-purple-700 hover:bg-purple-800 text-white px-6 py-3 rounded-lg font-semibold w-full sm:w-auto transition"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* PAYMENT */}
        {currentStep === "payment" && (
          <div className="max-w-lg mx-auto bg-white rounded-2xl p-6 shadow-md text-center">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              {isBalanceMode
                ? "Pay Balance"
                : isShipmentMode
                  ? "Pay Shipment Fee"
                  : "Payment Information"}
            </h2>

            {USE_STATIC_VA_FOR_TEST && (
              <div>
                {/* <p className="text-xs text-blue-700 mt-1">
                  This payment flow will request a <b>STATIC</b> virtual account
                </p> */}
              </div>
            )}

            {isShipmentMode || isBalanceMode ? (
              <div
                className={`text-left border rounded-lg p-4 mb-4 ${
                  isShipmentMode
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <p className="font-semibold text-gray-800 mb-1">
                  {isBalanceMode ? "Balance Payment" : "Shipment Payment"}
                </p>
                <p className="text-sm text-gray-700">
                  Order ID:{" "}
                  <span className="font-mono">
                    {isBalanceMode
                      ? balanceOrderId || "—"
                      : shipmentOrderId || "—"}
                  </span>
                </p>

                <p className="text-sm text-gray-700 mt-2">
                  Amount to Pay (Base):{" "}
                  <b>₦{Number(payableBaseAmount || 0).toLocaleString()}</b>
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  VAT (0.4% capped ₦300):{" "}
                  <b>₦{Number(payableVatAmount || 0).toLocaleString()}</b>
                </p>
                <p className="text-sm text-gray-900 mt-1">
                  Total (Base + VAT): <b>₦{payableAmount.toLocaleString()}</b>
                </p>
              </div>
            ) : (
              <div className="text-left bg-gray-50 border rounded-lg p-4 mb-4">
                <p className="font-semibold text-gray-800 mb-1">
                  Order Summary
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Amount to Pay (Base):{" "}
                  <b>₦{Number(payableBaseAmount || 0).toLocaleString()}</b>
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  VAT (0.4% capped ₦300):{" "}
                  <b>₦{payableVatAmount.toLocaleString()}</b>
                </p>
                <p className="text-sm text-gray-900 mt-1">
                  Total (Base + VAT): <b>₦{payableAmount.toLocaleString()}</b>
                </p>
              </div>
            )}

            {!isPaymentOnlyMode ? (
              <p className="text-gray-700 mb-4">
                Click <strong>Pay Now</strong> to create your order and generate
                your UBA virtual account.
              </p>
            ) : null}

            <div className="text-left bg-gray-50 border rounded-lg p-4 mb-4">
              {/* <p className="font-semibold text-gray-800 mb-2">Bank Transfer Details</p> */}

              {!USE_STATIC_VA_FOR_TEST ? (
                <p className="text-xs text-gray-600 mb-2">
                  Transfer window: <b>{VA_EXPIRY_MINUTES} minutes</b>{" "}
                  {VA_EXPIRY_MINUTES % 60 === 0 ? (
                    <span className="text-gray-500">
                      ({VA_EXPIRY_MINUTES / 60} hour
                      {VA_EXPIRY_MINUTES / 60 === 1 ? "" : "s"})
                    </span>
                  ) : null}
                  . After expiry, generate a new virtual account.
                </p>
              ) : (
                <p className="text-xs text-gray-600 mb-2">
                  {/* Static virtual account mode: no expiry. */}
                </p>
              )}

              {virtualAccount?.vNUBAN ? (
                <>
                  {/* <p className="text-gray-700">
        A virtual account has been generated. Transfer the exact amount to the account.
      </p> */}

                  {/* ✅ CLEAN ORDER REF */}
                  <div className="text-sm text-gray-700 mt-3">
                    <span className="font-semibold">Order Ref:</span>{" "}
                    <span className="font-mono">
                      {isBalanceMode
                        ? balanceOrderId || "—"
                        : isShipmentMode
                          ? shipmentOrderId || "—"
                          : pendingOrderId || "—"}
                    </span>
                  </div>

                  {/* ✅ EXPIRY / TYPE */}
                  {vaExpireAt ? (
                    <div className="text-xs text-gray-500 mt-1">
                      Expires: <span className="font-mono">{vaExpireAt}</span>
                      {vaRemainingLabel && (
                        <span className="ml-2 px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                          {vaRemainingLabel}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-1">
                      {/* Type:{" "}
          <span className="font-semibold">
            {virtualAccount?.type || (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC")}
          </span> */}
                    </div>
                  )}

                  {/* ✅ ACTION BUTTONS (CLEAN + FOCUSED) */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      onClick={reconcileOrderPayment}
                      disabled={reconLoading}
                      className={`${
                        reconLoading
                          ? "bg-gray-400"
                          : "bg-green-700 hover:bg-green-800"
                      } text-white px-4 py-2 rounded-lg font-semibold transition`}
                    >
                      {reconLoading ? "Checking..." : "I have paid (Confirm)"}
                    </button>

                    {!isPaymentOnlyMode && (
                      <button
                        onClick={handleCancelPendingPayment}
                        className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-lg font-semibold transition"
                      >
                        Cancel Pending Payment
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <p>
                  {/* No virtual account yet. Click <strong>Pay Now</strong> to generate it. */}
                </p>
              )}
            </div>

            {/* ✅ NOTE (OPTIONAL) + PERMANENT LOCK after sending */}
            <div className="text-left mt-2">
              <label
                htmlFor="note"
                className="block text-gray-700 font-medium mb-2"
              >
                Note for the Merchant{" "}
                <span className="text-gray-500 font-normal">(Optional)</span>
              </label>

              <textarea
                id="note"
                rows="3"
                className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-purple-400 outline-none ${
                  noteLocked ? "bg-gray-100 text-gray-500" : ""
                }`}
                placeholder="Optional: Add any special instruction or note and your phone number..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={noteLocked}
              />

              <button
                onClick={() =>
                  handleSaveNote(
                    isBalanceMode
                      ? balanceOrderDoc
                      : isShipmentMode
                        ? shipmentOrderDoc
                        : null,
                  )
                }
                disabled={savingNote || noteLocked}
                className={`mt-3 font-semibold px-6 py-2 rounded-lg transition w-full sm:w-auto ${
                  noteLocked
                    ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                    : savingNote
                      ? "bg-gray-500 text-white opacity-70 cursor-not-allowed"
                      : "bg-gray-700 hover:bg-gray-800 text-white"
                }`}
                title={
                  noteLocked
                    ? "Note already sent for this order"
                    : !orderIdForNotes
                      ? "Click Pay Now first to create the order, then you can add an optional note."
                      : "Send note"
                }
              >
                {noteLocked
                  ? "Note Sent ✅"
                  : savingNote
                    ? "Sending..."
                    : "Add Note (Optional)"}
              </button>

              {!orderIdForNotes && !isPaymentOnlyMode ? (
                <p className="text-xs text-gray-500 mt-2">
                  Note can be sent after you click <b>Pay Now</b> (so we can
                  attach it to the order and lock it).
                </p>
              ) : null}
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
              {!isPaymentOnlyMode ? (
                <button
                  onClick={goToPreviousStep}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-3 rounded-lg font-semibold w-full sm:w-auto transition"
                >
                  Back
                </button>
              ) : (
                <button
                  onClick={() => navigate(-1)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-3 rounded-lg font-semibold w-full sm:w-auto transition"
                >
                  Back
                </button>
              )}

              <button
                onClick={handlePayNow}
                disabled={vaLoading}
                className={`${
                  vaLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                } text-white px-5 py-3 rounded-lg font-semibold w-full sm:w-auto transition`}
              >
                {vaLoading
                  ? "Processing..."
                  : isBalanceMode
                    ? "Pay Balance Now"
                    : isShipmentMode
                      ? "Pay Shipment Now"
                      : "Pay Now"}
              </button>
            </div>
          </div>
        )}

        {/* Virtual Account Modal */}
        {showVAModal && virtualAccount && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowVAModal(false)}
            />

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-60 p-6">
              <button
                onClick={() => setShowVAModal(false)}
                className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <h3 className="text-xl font-semibold mb-3">
                Virtual Account Details
              </h3>

              <div className="space-y-2 text-left">
                <div>
                  <p className="text-sm text-gray-500">Bank</p>
                  <p className="font-medium">UBA</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Order Ref</p>
                  <p className="font-mono">
                    {isBalanceMode
                      ? balanceOrderId || "—"
                      : isShipmentMode
                        ? shipmentOrderId || "—"
                        : pendingOrderId || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Account Number</p>
                  <p className="font-mono text-lg tracking-wider">
                    {virtualAccount.vNUBAN}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Account Name</p>
                  <p className="font-medium">{virtualAccount.accountName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Account Type</p>
                  <p className="font-medium">
                    {virtualAccount?.type ||
                      (USE_STATIC_VA_FOR_TEST ? "STATIC" : "DYNAMIC")}
                  </p>
                </div>

                {/* ✅ Breakdown */}
                <div className="bg-gray-50 border rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm text-gray-700">
                    <span>{payableLabel} (Base)</span>
                    <span className="font-semibold">
                      {formatNaira(payableBaseAmount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-700 mt-1">
                    <span>VAT (0.4% capped ₦300)</span>
                    <span className="font-semibold">
                      {formatNaira(payableVatAmount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-900 mt-2">
                    <span className="font-semibold">Total to Pay</span>
                    <span className="font-bold">
                      {formatNaira(payableAmount)}
                    </span>
                  </div>
                </div>

                {vaExpireAt ? (
                  <div>
                    <p className="text-sm text-gray-500">Expires</p>
                    <p className="font-mono">{vaExpireAt}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Transfer window: <b>{VA_EXPIRY_MINUTES} minutes</b>{" "}
                      {VA_EXPIRY_MINUTES % 60 === 0 ? (
                        <span className="text-gray-500">
                          ({VA_EXPIRY_MINUTES / 60} hour
                          {VA_EXPIRY_MINUTES / 60 === 1 ? "" : "s"})
                        </span>
                      ) : null}
                      {vaRemainingLabel ? (
                        <>
                          {" "}
                          •{" "}
                          <span className="font-semibold">
                            {vaRemainingLabel}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>
                ) : (
                  <div />
                )}

                <p className="text-xs text-gray-500">
                  Transfer to this account, then click{" "}
                  <b>I have paid (Confirm)</b>.
                </p>
              </div>

              <div className="mt-5 flex justify-end gap-2 flex-wrap">
                <button
                  onClick={() => handleCopy(virtualAccount.vNUBAN)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold transition inline-flex items-center gap-2"
                >
                  <FaCopy /> Copy
                </button>

                <button
                  onClick={reconcileOrderPayment}
                  disabled={reconLoading}
                  className={`${
                    reconLoading
                      ? "bg-gray-400"
                      : "bg-green-700 hover:bg-green-800"
                  } text-white px-4 py-2 rounded-lg font-semibold transition`}
                >
                  {reconLoading ? "Checking..." : "I have paid (Confirm)"}
                </button>

                {!isPaymentOnlyMode ? (
                  <button
                    onClick={handleCancelPendingPayment}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-lg font-semibold transition"
                  >
                    Cancel Pending
                  </button>
                ) : null}

                <button
                  onClick={() => setShowVAModal(false)}
                  className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg font-semibold transition"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Smooth Professional Success Modal */}
        {showThankYouModal && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[999] flex items-center justify-center px-4"
            style={{ animation: "tyFadeIn 180ms ease-out both" }}
          >
            <div
              className="fixed inset-0 bg-black/70"
              onClick={handleThankYouOk}
              style={{ animation: "tyBackdrop 220ms ease-out both" }}
            />

            <div
              className="relative w-full max-w-md mx-auto overflow-hidden rounded-3xl bg-white shadow-2xl border border-white/70"
              style={{ animation: "tyPop 360ms cubic-bezier(.2,.9,.2,1) both" }}
            >
              <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-green-500 via-emerald-500 to-lime-400" />
              <div className="absolute -top-20 -right-20 w-44 h-44 rounded-full bg-green-100 opacity-80" />
              <div className="absolute -bottom-20 -left-20 w-44 h-44 rounded-full bg-emerald-50 opacity-90" />

              <button
                onClick={handleThankYouOk}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 flex items-center justify-center transition"
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <div className="relative px-6 pt-9 pb-7 sm:px-8 sm:pt-10 sm:pb-8 text-center">
                <div
                  className="mx-auto mb-5 relative w-24 h-24 flex items-center justify-center"
                  style={{ animation: "tyFloat 2.2s ease-in-out infinite" }}
                >
                  <div
                    className="absolute inset-0 rounded-full bg-green-200"
                    style={{ animation: "tyRing 1100ms ease-out infinite" }}
                  />
                  <div
                    className="absolute inset-2 rounded-full bg-green-100"
                    style={{
                      animation: "tyRing 1100ms ease-out infinite 180ms",
                    }}
                  />
                  <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-700 flex items-center justify-center text-white shadow-xl shadow-green-200">
                    <FaCheck
                      className="text-3xl"
                      style={{ animation: "tyCheck 420ms ease-out both 160ms" }}
                    />
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-bold uppercase tracking-wide mb-3">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Confirmed
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                  Payment Received
                </h3>

                <p className="text-gray-600 mt-3 leading-relaxed">
                  We’ve successfully received your{" "}
                  <span className="font-semibold text-gray-900">
                    {thankYouData?.mode === "shipment"
                      ? "shipment payment"
                      : thankYouData?.mode === "balance"
                        ? "balance payment"
                        : "order payment"}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-green-700">
                    {formatNaira(thankYouData?.amount)}
                  </span>
                  .
                </p>

                <div className="w-full mt-6 bg-gray-50 border border-gray-200 rounded-2xl p-4 text-left shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">
                      Order Reference
                    </span>
                    <span className="text-sm font-mono text-gray-900 text-right break-all">
                      {thankYouData?.orderId || "—"}
                    </span>
                  </div>

                  <div className="h-px bg-gray-200 my-3" />

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">Payment Type</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">
                      {thankYouData?.mode === "shipment"
                        ? "Shipment Fee"
                        : thankYouData?.mode === "balance"
                          ? "Balance Payment"
                          : "Order Payment"}
                    </span>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl bg-green-50 border border-green-100 px-4 py-3 text-green-800 text-sm leading-relaxed">
                  Thank you for your patronage. Your order record has been
                  updated and processing can continue immediately.
                </div>

                <div className="mt-6 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-600"
                    style={{ animation: "tyProgress 2.8s linear both" }}
                  />
                </div>

                <button
                  onClick={handleThankYouOk}
                  className="mt-5 w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white py-3.5 rounded-2xl font-bold transition shadow-lg shadow-green-100"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Persistent Failure Modal */}
        {showPaymentFailureModal && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[999] flex items-center justify-center px-4"
          >
            <div
              className="fixed inset-0 bg-black/70"
              onClick={handlePaymentFailureOk}
            />

            <div className="relative w-full max-w-md mx-auto overflow-hidden rounded-3xl bg-white shadow-2xl border border-white/70">
              <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-red-500 via-rose-500 to-orange-400" />

              <button
                onClick={handlePaymentFailureOk}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 flex items-center justify-center transition"
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <div className="relative px-6 pt-9 pb-7 sm:px-8 sm:pt-10 sm:pb-8 text-center">
                <div className="mx-auto mb-5 w-20 h-20 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <FaTimes className="text-3xl" />
                </div>

                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold uppercase tracking-wide mb-3">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  Not Confirmed
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                  Payment Not Confirmed
                </h3>

                <p className="text-gray-600 mt-3 leading-relaxed">
                  {paymentFailureData?.message ||
                    "We could not confirm this payment yet."}
                </p>

                <div className="w-full mt-6 bg-gray-50 border border-gray-200 rounded-2xl p-4 text-left shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">
                      Order Reference
                    </span>
                    <span className="text-sm font-mono text-gray-900 text-right break-all">
                      {paymentFailureData?.orderId || "—"}
                    </span>
                  </div>

                  <div className="h-px bg-gray-200 my-3" />

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">Payment Type</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">
                      {paymentFailureData?.mode === "shipment"
                        ? "Shipment Fee"
                        : paymentFailureData?.mode === "balance"
                          ? "Balance Payment"
                          : "Order Payment"}
                    </span>
                  </div>

                  <div className="h-px bg-gray-200 my-3" />

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">Amount</span>
                    <span className="text-sm font-bold text-gray-900 text-right">
                      {formatNaira(paymentFailureData?.amount)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handlePaymentFailureOk}
                  className="mt-6 w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white py-3.5 rounded-2xl font-bold transition shadow-lg shadow-red-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;